import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";
import type {
  AsylumDocument,
  EmploymentDocument,
  FamilyDocument,
  OtherDocument,
  StayDocument,
} from "../../context/FormContext";
import {
  mapModalResidenceDocTypeToDocumentType,
  mapModalResidenceDocTypeToStayTierKey,
  mapStayTierKeyToDocumentType,
} from "../../constants/caseDocumentSections";
import { mvpApi } from "../../../services/api";
import { uploadCaseDocumentFile, uploadCaseDocumentFileWithRetry } from "../../utils/uploadCaseDocument";
import {
  hasPassportPdfUploaded,
  isIdentityCategoryComplete,
  shouldAutoSkipPassportIdentityGate,
  shouldShowPassportIdentityDetailForm,
  shouldShowPassportUploadLanding,
} from "../../utils/identityProgress";
import MobileDocumentScanner from "./MobileDocumentScanner";
import PassportScanExamplesModal from "../ui/PassportScanExamplesModal";
import DocPhotoGuideModal from "../ui/DocPhotoGuideModal";
import DateInput from "../ui/DateInput";
import TieredSelect from "../ui/TieredSelect";
import SearchableDropdown from "../ui/SearchableDropdown";
import ButtonChoice from "../ui/ButtonChoice";
import UploadOptions from "./UploadOptions";
import CriminalUpload from "./CriminalUpload";
import SectionExitModal from "../sections/SectionExitModal";
import DocumentDeleteConfirmModal from "../sections/DocumentDeleteConfirmModal";
import {
  deleteSessionUploadedFileIfApplicable,
  resolveMongoDocumentIdForDelete,
} from "../../utils/sessionDocumentDelete";
import {
  inferWorkTypeFromUploadedEmploymentDocs,
  type DocumentSectionAckKey,
} from "../../utils/deriveDocumentChecklistHints";
import { COUNTRIES } from "../../mocks/applicantData";
import { OTHER_DOC_TYPES } from "../../../mocks/cases";
import { DOCUMENT_FILE_ACCEPT, validateClientDocumentFile } from "../../../utils/documentUploadPolicy";

interface UploadedDoc {
  /** Same id as `StayDocument` for checklist + removal sync */
  id: string;
  filename: string;
  docType: string;
  description?: string;
}

function genStayDocId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function formatStayFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Uses {@link resolveMongoDocumentIdForDelete}; stay rows may expose `documentId` or Mongo `id`. */
function resolveStayMongoDocumentId(stay: StayDocument | undefined): string | null {
  return resolveMongoDocumentIdForDelete(stay);
}

/** Non-empty filename / upload field. */
function hasTrimmedString(v: unknown): boolean {
  return Boolean(String(v ?? "").trim());
}

function hasUploadedDocInRows(docs: readonly { filename?: string }[] | undefined): boolean {
  return (docs ?? []).some((d) => hasTrimmedString(d.filename));
}

function hasCriminalPageFileUpload(data: {
  criminalRecordDocUpload?: string;
  criminalCertificates?: { status: string; filename?: string }[];
}): boolean {
  if (hasTrimmedString(data.criminalRecordDocUpload)) return true;
  return (data.criminalCertificates ?? []).some(
    (c) => c.status === "uploaded" && hasTrimmedString(c.filename)
  );
}

interface DocUploadProps {
  category: string;
  onDone: () => void;
  onBack: () => void;
  /** After "Save and finish later": un-ack section, then advance to next incomplete category (or parent closes to organizer). */
  onSaveAndFinishLater?: (sectionKey: DocumentSectionAckKey) => void;
}

/* ─── Single-file upload categories ─── */
const SINGLE_FILE_CATEGORIES = ["denial"] as const;
type SingleFileCategory = (typeof SINGLE_FILE_CATEGORIES)[number];

const FIELD_MAP: Record<SingleFileCategory | "identity", string> = {
  identity: "docUpload",
  denial: "denialResolutionUpload",
};

interface FamilyUploadProps {
  onBack: () => void;
  /** Advance without “mark section complete” modal (Skip path). */
  onSkipDone: () => void;
  /** Open acknowledgment modal, then advance on confirm (Continue path). */
  onContinueWithAck: () => void;
}

interface FamilyCategoryDef {
  key: string;
  labelKey: string;
  helperKey: string;
  hasDescription: boolean;
}

const FAMILY_CATEGORIES: FamilyCategoryDef[] = [
  {
    key: "marriage",
    labelKey: "docUpload.familyDocMarriage",
    helperKey: "docUpload.familyDocMarriageHelper",
    hasDescription: false,
  },
  {
    key: "libro",
    labelKey: "docUpload.familyDocLibro",
    helperKey: "docUpload.familyDocLibroHelper",
    hasDescription: false,
  },
  {
    key: "birth",
    labelKey: "docUpload.familyDocBirth",
    helperKey: "docUpload.familyDocBirthHelper",
    hasDescription: false,
  },
  {
    key: "cohabitation",
    labelKey: "docUpload.familyDocCohabitation",
    helperKey: "docUpload.familyDocCohabitationHelper",
    hasDescription: false,
  },
  {
    key: "school",
    labelKey: "docUpload.familyDocSchool",
    helperKey: "docUpload.familyDocSchoolHelper",
    hasDescription: false,
  },
  {
    key: "other",
    labelKey: "docUpload.familyDocOther",
    helperKey: "docUpload.familyDocOtherHelper",
    hasDescription: true,
  },
];

function mapFamilyCategoryToDocumentType(category: string): string {
  switch (category) {
    case "marriage":
      return "marriage_certificate_id";
    case "libro":
      return "libro_de_familia_id";
    case "birth":
      return "childrens_birth_certificates_id";
    case "cohabitation":
      return "proof_of_cohabitation_id";
    case "school":
      return "school_enrollment_id";
    default:
      return "other_documents_id";
  }
}

interface FamilyDocCardProps {
  doc: FamilyDocument;
  hasDescription: boolean;
  onRemove: (id: string) => void;
  onUpdateDesc: (id: string, desc: string) => void;
  onFileChange: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: (el: HTMLInputElement | null) => void;
  t: (key: string) => string;
}

function FamilyDocCard({
  doc,
  hasDescription,
  onRemove,
  onUpdateDesc,
  onFileChange,
  fileInputRef,
  t,
}: FamilyDocCardProps) {
  const inputId = `family-file-${doc.id}`;

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-2 overflow-hidden">
      <div className="px-4 py-3">
        {hasDescription ? (
          <input
            type="text"
            maxLength={50}
            value={doc.description}
            onChange={(e) => onUpdateDesc(doc.id, e.target.value)}
            placeholder={t("docUpload.familyOtherPlaceholder")}
            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300 mb-3"
          />
        ) : null}

        {!doc.filename ? (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 gap-2 min-w-0">
              <button
                type="button"
                onClick={() => document.getElementById(inputId)?.click()}
                className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-file-upload-line text-sm" />
                {t("section7.uploadBtn")}
              </button>
              <button
                type="button"
                onClick={() => document.getElementById(inputId)?.click()}
                className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-camera-line text-sm" />
                {t("section6.cameraBtn")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => onRemove(doc.id)}
              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 cursor-pointer transition-colors"
              title={t("docUpload.removeDoc")}
            >
              <i className="ri-delete-bin-line text-base" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
            <i className="ri-file-check-line text-emerald-500 text-sm shrink-0" />
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <p className="text-[12px] font-medium text-emerald-800 truncate">{doc.filename}</p>
              {doc.fileSize ? (
                <span className="text-[11px] text-emerald-600 shrink-0">{doc.fileSize}</span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => document.getElementById(inputId)?.click()}
              className="text-[11px] text-emerald-600 font-medium underline underline-offset-2 cursor-pointer hover:text-emerald-700 shrink-0 whitespace-nowrap"
            >
              {t("section6.changeFile")}
            </button>
            <button
              type="button"
              onClick={() => onRemove(doc.id)}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
              title={t("docUpload.removeDoc")}
            >
              <i className="ri-delete-bin-line text-base" />
            </button>
          </div>
        )}

        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => onFileChange(doc.id, e)}
          className="hidden"
        />
      </div>
    </div>
  );
}

function FamilyUpload({ onBack, onSkipDone, onContinueWithAck }: FamilyUploadProps) {
  const { t } = useLang();
  const { data, update, ensureCaseSession, caseSessionId } = useForm();
  const familyFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [familyOtherDescription, setFamilyOtherDescription] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const employmentDescSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const hasFamilyDocUpload = hasUploadedDocInRows(data.familyDocuments);
  const [familyDeleteConfirmCategory, setFamilyDeleteConfirmCategory] = useState<string | null>(null);
  const [familyDeleteBusy, setFamilyDeleteBusy] = useState(false);

  useEffect(() => {
    const existingOther = data.familyDocuments.find((doc) => doc.category === "other");
    setFamilyOtherDescription(existingOther?.description ?? "");
  }, [data.familyDocuments]);

  const cleanupEmptyDocs = () => {
    const cleaned = data.familyDocuments.filter((doc) => !!doc.filename);
    if (cleaned.length !== data.familyDocuments.length) {
      update("familyDocuments", cleaned);
    }
  };

  const docsForCategory = (cat: string) =>
    data.familyDocuments.filter((doc) => doc.category === cat);

  const removeFamilyCategoryDoc = (category: string) => {
    update("familyDocuments", data.familyDocuments.filter((doc) => doc.category !== category));
  };

  const performFamilyRemoveAfterConfirm = async () => {
    const category = familyDeleteConfirmCategory;
    if (!category) return;
    const doc = docsForCategory(category)[0];
    if (!doc?.filename) {
      setFamilyDeleteConfirmCategory(null);
      return;
    }
    setFamilyDeleteBusy(true);
    setUploadError(null);
    try {
      const mid = resolveMongoDocumentIdForDelete(doc);
      const res = await deleteSessionUploadedFileIfApplicable({
        ensureCaseSession,
        caseSessionId,
        mongoDocumentId: mid,
      });
      if (res.ok === false) {
        setUploadError(res.message);
        return;
      }
      removeFamilyCategoryDoc(category);
    } finally {
      setFamilyDeleteBusy(false);
      setFamilyDeleteConfirmCategory(null);
    }
  };

  const handleFamilyCategoryFileChange = async (category: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    const result = await uploadCaseDocumentFile(
      ensureCaseSession,
      mapFamilyCategoryToDocumentType(category),
      file,
      {
        supportingDescription: category === "other" ? familyOtherDescription.trim() || undefined : undefined,
      }
    );

    if (result.ok === false) {
      setUploadError(result.message);
      if (familyFileInputRefs.current[category]) {
        familyFileInputRefs.current[category]!.value = "";
      }
      return;
    }

    const nextDoc: FamilyDocument = {
      id: `family-${category}-${Date.now()}`,
      category,
      filename: file.name,
      fileSize: formatStayFileSize(file.size),
      description: category === "other" ? familyOtherDescription.trim() : "",
      documentId: result.documentId ? String(result.documentId).trim() : undefined,
    };
    update("familyDocuments", [
      ...data.familyDocuments.filter((doc) => doc.category !== category),
      nextDoc,
    ]);
    if (familyFileInputRefs.current[category]) familyFileInputRefs.current[category]!.value = "";
  };

  return (
    <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-6">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button
          type="button"
          onClick={() => {
            cleanupEmptyDocs();
            onBack();
          }}
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          {t("nav.breadcrumbOrganizer")}
        </button>
      </nav>

      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-900 mb-1">{t("docUpload.familyHeading")}</h2>
        <p className="text-[13px] text-slate-600 leading-relaxed">{t("docUpload.familyDesc")}</p>
      </div>

      {uploadError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
          {uploadError}
        </p>
      ) : null}

      {FAMILY_CATEGORIES.map((cat) => {
        const categoryDoc = docsForCategory(cat.key)[0];

    return (
          <div key={cat.key} className="mb-5">
            <p className="text-[15px] font-medium text-slate-800 mt-5 mb-1">{t(cat.labelKey)}</p>
            <p className="text-[12px] text-slate-500 mb-3 leading-relaxed">{t(cat.helperKey)}</p>

            {cat.hasDescription ? (
              <input
                type="text"
                maxLength={80}
                value={familyOtherDescription}
                onChange={(e) => setFamilyOtherDescription(e.target.value)}
                placeholder={t("docUpload.familyOtherPlaceholder")}
                className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300 mb-3"
              />
            ) : null}

            {!categoryDoc?.filename ? (
              <div className="grid grid-cols-1 gap-2">
        <button
          type="button"
                  onClick={() => familyFileInputRefs.current[cat.key]?.click()}
                  className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors"
        >
                  <i className="ri-file-upload-line text-sm" />
                  {t("docUpload.chooseFile")}
        </button>
                {/* "Use scanner" removed — duplicated file picker; portal has no bundled scanner */}
          </div>
            ) : (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                <i className="ri-file-check-line text-emerald-600 text-base shrink-0" />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="text-[12px] font-medium text-emerald-800 truncate">{categoryDoc.filename}</p>
                  {categoryDoc.fileSize ? (
                    <span className="text-[11px] text-emerald-700 shrink-0">{categoryDoc.fileSize}</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => familyFileInputRefs.current[cat.key]?.click()}
                  className="text-[12px] text-emerald-700 underline cursor-pointer shrink-0 whitespace-nowrap"
                >
                  {t("docUpload.modalChangeFile")}
                </button>
                <button
                  type="button"
                  onClick={() => setFamilyDeleteConfirmCategory(cat.key)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0"
                  title={t("docUpload.removeDoc")}
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
        </div>
            )}

            <input
              ref={(el) => {
                familyFileInputRefs.current[cat.key] = el;
              }}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => handleFamilyCategoryFileChange(cat.key, e)}
              className="hidden"
            />
            </div>
        );
      })}

      <div className="bg-slate-50 rounded-2xl p-3 mb-3 mt-2">
        <p className="text-[13px] font-semibold text-slate-700 mb-2">{t("docUpload.familyTipsTitle")}</p>
        <ul className="flex flex-col gap-1.5">
          {(["familyTipOptional", "familyTipApostille", "familyTipSkip"] as const).map((key) => (
            <li key={key} className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-slate-500 text-sm mt-0.5 shrink-0" />
              <p className="text-[12px] text-slate-500 leading-relaxed">{t(`docUpload.${key}`)}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => {
            cleanupEmptyDocs();
            if (hasFamilyDocUpload) onContinueWithAck();
            else onSkipDone();
          }}
          className="w-full min-h-[48px] flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
        >
          {hasFamilyDocUpload ? t("docFlow.continue") : t("docFlow.skip")}
        </button>
        <button
          type="button"
          onClick={() => {
            cleanupEmptyDocs();
            onBack();
          }}
          className="w-full min-h-[48px] flex items-center justify-center bg-white border border-slate-200 hover:border-slate-300 text-slate-800 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
        >
          {t("docFlow.backToChecklist")}
        </button>
      </div>

      <DocumentDeleteConfirmModal
        open={familyDeleteConfirmCategory !== null}
        busy={familyDeleteBusy}
        onCancel={() => !familyDeleteBusy && setFamilyDeleteConfirmCategory(null)}
        onConfirm={() => void performFamilyRemoveAfterConfirm()}
      />
    </div>
  );
}

interface EmploymentUploadProps {
  onBack: () => void;
  onSkipDone: () => void;
  onContinueWithAck: () => void;
}

interface EmploymentCategoryDef {
  key: string;
  labelKey: string;
  helperKey: string;
  hasDescription: boolean;
}

const EMPLOYMENT_CATEGORIES: EmploymentCategoryDef[] = [
  {
    key: "contract",
    labelKey: "docUpload.empDocContract",
    helperKey: "docUpload.empDocContractHelper",
    hasDescription: false,
  },
  {
    key: "payslip",
    labelKey: "docUpload.empDocPayslip",
    helperKey: "docUpload.empDocPayslipHelper",
    hasDescription: false,
  },
  {
    key: "vidalaboral",
    labelKey: "docUpload.empDocVidaLaboral",
    helperKey: "docUpload.empDocVidaLaboralHelper",
    hasDescription: false,
  },
  {
    key: "joboffer",
    labelKey: "docUpload.empDocJobOffer",
    helperKey: "docUpload.empDocJobOfferHelper",
    hasDescription: false,
  },
  {
    key: "other",
    labelKey: "docUpload.empDocOther",
    helperKey: "docUpload.empDocOtherHelper",
    hasDescription: true,
  },
];

function mapEmploymentCategoryToDocumentType(category: string): string {
  switch (category) {
    case "contract":
      return "work_contract_id";
    case "payslip":
      return "nomina_id";
    case "vidalaboral":
      return "vida_laboral_id";
    case "joboffer":
      return "job_offer_letter_id";
    default:
      return "other_documents_id";
  }
}

interface EmpDocCardProps {
  doc: EmploymentDocument;
  hasDescription: boolean;
  onRemove: (id: string) => void;
  onUpdateDesc: (id: string, desc: string) => void;
  onFileChange: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: (el: HTMLInputElement | null) => void;
  t: (key: string) => string;
  onScanPress?: () => void;
  scannerBtnLabel?: string;
}

function EmpDocCard({
  doc,
  hasDescription,
  onRemove,
  onUpdateDesc,
  onFileChange,
  fileInputRef,
  t,
  onScanPress,
  scannerBtnLabel,
}: EmpDocCardProps) {
  const inputId = `emp-file-${doc.id}`;

  const scanSecondary = !!onScanPress;

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-2 overflow-hidden">
      <div className="px-4 py-3">
        {hasDescription ? (
          <input
            type="text"
            maxLength={50}
            value={doc.description}
            onChange={(e) => onUpdateDesc(doc.id, e.target.value)}
            placeholder={t("docUpload.empOtherPlaceholder")}
            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300 mb-3"
          />
        ) : null}

        {!doc.filename ? (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 gap-2 min-w-0">
              <button
                type="button"
                onClick={() => document.getElementById(inputId)?.click()}
                className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-file-upload-line text-sm" />
                {t("section7.uploadBtn")}
              </button>
              <button
                type="button"
                onClick={() =>
                  scanSecondary ? onScanPress?.() : document.getElementById(inputId)?.click()}
                className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className={scanSecondary ? "ri-scan-line text-sm" : "ri-camera-line text-sm"} />
                {scannerBtnLabel || t("section6.cameraBtn")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => onRemove(doc.id)}
              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 cursor-pointer transition-colors"
              title={t("docUpload.removeDoc")}
            >
              <i className="ri-delete-bin-line text-base" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
            <i className="ri-file-check-line text-emerald-500 text-sm shrink-0" />
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <p className="text-[12px] font-medium text-emerald-800 truncate">{doc.filename}</p>
              {doc.fileSize ? (
                <span className="text-[11px] text-emerald-600 shrink-0">{doc.fileSize}</span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => document.getElementById(inputId)?.click()}
              className="text-[11px] text-emerald-600 font-medium underline underline-offset-2 cursor-pointer hover:text-emerald-700 shrink-0 whitespace-nowrap"
            >
              {t("section6.changeFile")}
            </button>
            <button
              type="button"
              onClick={() => onRemove(doc.id)}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
              title={t("docUpload.removeDoc")}
            >
              <i className="ri-delete-bin-line text-base" />
            </button>
          </div>
        )}

        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => onFileChange(doc.id, e)}
          className="hidden"
        />
        </div>
    </div>
  );
}

function EmploymentUpload({ onBack, onSkipDone, onContinueWithAck }: EmploymentUploadProps) {
  const { t } = useLang();
  const { data, update, ensureCaseSession, caseSessionId } = useForm();
  const empFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const employmentDescSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const empScanTargetIdRef = useRef<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [empScannerOpen, setEmpScannerOpen] = useState(false);
  /** Always show the Employed / Self-employed question first; then document uploads. */
  const [empPhase, setEmpPhase] = useState<"gate" | "documents">("gate");
  const [gateChoice, setGateChoice] = useState<"" | "employed" | "selfemployed">("");
  const hasEmploymentDocUpload = hasUploadedDocInRows(data.employmentDocuments);
  const [empDeleteConfirmId, setEmpDeleteConfirmId] = useState<string | null>(null);
  const [empDeleteBusy, setEmpDeleteBusy] = useState(false);

  useEffect(() => {
    const fromForm = data.workType === "employed" || data.workType === "selfemployed" ? data.workType : "";
    const fromDocs = inferWorkTypeFromUploadedEmploymentDocs(data.employmentDocuments);
    const suggested = fromForm || fromDocs;
    setGateChoice((prev) => {
      if (prev === "employed" || prev === "selfemployed") return prev;
      return suggested || "";
    });
  }, [data.workType, data.employmentDocuments]);

  const effectiveWorkType: "employed" | "selfemployed" | "" =
    data.workType === "employed" || data.workType === "selfemployed"
      ? data.workType
      : gateChoice === "employed" || gateChoice === "selfemployed"
        ? gateChoice
        : "";

  const cleanupEmptyDocs = () => {
    const cleaned = data.employmentDocuments.filter((doc) => !!doc.filename);
    if (cleaned.length !== data.employmentDocuments.length) update("employmentDocuments", cleaned);
  };

  const docsForCategory = (cat: string) => data.employmentDocuments.filter((doc) => doc.category === cat);

  const addEmpDoc = (cat: string): EmploymentDocument => {
    const newDoc: EmploymentDocument = { id: `emp-${Date.now()}`, category: cat, filename: "", fileSize: "", description: "" };
    update("employmentDocuments", [...data.employmentDocuments, newDoc]);
    return newDoc;
  };

  const removeEmpDoc = (id: string) =>
    update("employmentDocuments", data.employmentDocuments.filter((doc) => doc.id !== id));

  const performEmpRemoveAfterConfirm = async () => {
    const id = empDeleteConfirmId;
    if (!id) return;
    const doc = data.employmentDocuments.find((d) => d.id === id);
    if (!doc?.filename) {
      setEmpDeleteConfirmId(null);
      return;
    }
    setEmpDeleteBusy(true);
    setUploadError(null);
    try {
      const mid = resolveMongoDocumentIdForDelete(doc);
      const res = await deleteSessionUploadedFileIfApplicable({
        ensureCaseSession,
        caseSessionId,
        mongoDocumentId: mid,
      });
      if (res.ok === false) setUploadError(res.message);
      else removeEmpDoc(id);
    } finally {
      setEmpDeleteBusy(false);
      setEmpDeleteConfirmId(null);
    }
  };

  const updateEmpDesc = (id: string, description: string) => {
    update("employmentDocuments", data.employmentDocuments.map((doc) => (doc.id === id ? { ...doc, description } : doc)));
    const targetDoc = data.employmentDocuments.find((doc) => doc.id === id);
    if (!ensureCaseSession || !targetDoc?.filename) return;
    const timer = employmentDescSaveTimers.current[id];
    if (timer) clearTimeout(timer);
    employmentDescSaveTimers.current[id] = setTimeout(async () => {
      try {
        const sid = await ensureCaseSession();
        if (!sid) return;
        await mvpApi.updateSessionDocumentMetadata(sid, id, {
          supporting_description: description.trim() || "",
          self_employment_activity:
            data.workType === "selfemployed"
              ? data.selfEmploymentActivity.trim() || ""
              : "",
        });
      } catch {
        // Keep the local description; it can retry on the next edit.
      }
    }, 400);
  };

  const ingestEmpUpload = async (id: string, file: File) => {
    const targetDoc = data.employmentDocuments.find((doc) => doc.id === id);
    if (!targetDoc) return;

    setUploadError(null);
    const result = await uploadCaseDocumentFile(ensureCaseSession, mapEmploymentCategoryToDocumentType(targetDoc.category), file, {
      selfEmploymentActivity: data.workType === "selfemployed" ? data.selfEmploymentActivity.trim() || undefined : undefined,
      supportingDescription: targetDoc.description.trim() || undefined,
    });
    if (result.ok === false) {
      setUploadError(result.message);
      if (empFileInputRefs.current[id]) empFileInputRefs.current[id]!.value = "";
      return;
    }

    update(
      "employmentDocuments",
      data.employmentDocuments.map((doc) =>
        doc.id === id
          ? {
              ...doc,
              id: result.documentId || doc.id,
              filename: file.name,
              fileSize: formatStayFileSize(file.size),
              documentId: result.documentId ? String(result.documentId).trim() : doc.documentId,
            }
          : doc
      )
    );
    if (empFileInputRefs.current[id]) empFileInputRefs.current[id]!.value = "";
  };

  const handleEmpFileChange = async (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await ingestEmpUpload(id, file);
  };

  return (
    <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-6">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button type="button" onClick={() => { cleanupEmptyDocs(); onBack(); }} className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"><i className="ri-arrow-left-line text-base" aria-hidden="true" />{t("nav.breadcrumbOrganizer")}</button>
      </nav>

      {empPhase === "gate" ? (
        <>
          <h2 className="text-xl font-semibold text-slate-900 leading-tight mb-1">{t("docUpload.empHeading")}</h2>
          <p className="text-[13px] text-slate-500 leading-snug mb-6">{t("docUpload.workTypeGatePrompt")}</p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => {
                setGateChoice("employed");
                update("workType", "employed");
              }}
              className={`w-full text-left bg-white border rounded-2xl p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors cursor-pointer ${
                gateChoice === "employed" ? "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/40" : "border-slate-200"
              }`}
            >
              <p className="text-[15px] font-semibold text-slate-900 mb-0.5">{t("docUpload.workTypeEmployed")}</p>
              <p className="text-[12px] text-slate-500 leading-relaxed">{t("docUpload.workTypeEmployedHelper")}</p>
            </button>
            <button
              type="button"
              onClick={() => {
                setGateChoice("selfemployed");
                update("workType", "selfemployed");
              }}
              className={`w-full text-left bg-white border rounded-2xl p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors cursor-pointer ${
                gateChoice === "selfemployed" ? "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/40" : "border-slate-200"
              }`}
            >
              <p className="text-[15px] font-semibold text-slate-900 mb-0.5">{t("docUpload.workTypeSelfEmployed")}</p>
              <p className="text-[12px] text-slate-500 leading-relaxed">{t("docUpload.workTypeSelfEmployedHelper")}</p>
            </button>
          </div>
          <button
            type="button"
            disabled={gateChoice !== "employed" && gateChoice !== "selfemployed"}
            onClick={() => {
              if (gateChoice !== "employed" && gateChoice !== "selfemployed") return;
              update("workType", gateChoice);
              setEmpPhase("documents");
            }}
            className="w-full min-h-[48px] mt-6 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
          >
            {t("docUpload.empGateContinue")}
          </button>
        </>
      ) : (
        <>
          <h2 className="text-xl font-semibold text-slate-900 leading-tight mb-5">
            {effectiveWorkType === "employed" ? t("docUpload.empHeadingEmployed") : t("docUpload.empHeadingSelfEmployed")}
          </h2>
          {effectiveWorkType === "selfemployed" && (
            <div className="mb-5">
              <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">{t("docUpload.empDescriptionLabel")}</label>
              <input type="text" maxLength={25} value={data.selfEmploymentActivity} onChange={(e) => update("selfEmploymentActivity", e.target.value)} placeholder={t("docUpload.empDescriptionPlaceholder")} style={{ fontSize: "16px" }} className={`w-full h-10 px-3 bg-white border rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${data.selfEmploymentActivity ? "border-emerald-400" : "border-slate-200"}`} />
              <p className="text-[11px] text-slate-400 mt-1 text-right">{data.selfEmploymentActivity.length}/25</p>
            </div>
          )}
          {effectiveWorkType === "selfemployed" && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-3">
              <i className="ri-information-line text-slate-500 text-base mt-0.5 shrink-0" />
              <p className="text-[13px] text-slate-600 leading-relaxed">{t("docUpload.selfEmployedNote")}</p>
            </div>
          )}

          {uploadError ? <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">{uploadError}</p> : null}

          {EMPLOYMENT_CATEGORIES.filter((cat) => (effectiveWorkType === "selfemployed" ? cat.key === "other" : true)).map((cat) => {
            const catDocs = docsForCategory(cat.key);
            const lastDoc = catDocs[catDocs.length - 1];
            const canAddMore = !lastDoc || !!lastDoc.filename;
            return (
              <div key={cat.key} className="mb-5">
                <p className="text-[15px] font-medium text-slate-800 mt-5 mb-1">{t(cat.labelKey)}</p>
                <p className="text-[12px] text-slate-500 mb-3 leading-relaxed">{t(cat.helperKey)}</p>
                {catDocs.map((doc) => (
                  <EmpDocCard
                    key={doc.id}
                    doc={doc}
                    hasDescription={cat.hasDescription}
                    onRemove={(docId) => setEmpDeleteConfirmId(docId)}
                    onUpdateDesc={updateEmpDesc}
                    onFileChange={handleEmpFileChange}
                    fileInputRef={(el) => {
                      empFileInputRefs.current[doc.id] = el;
                    }}
                    t={t}
                    scannerBtnLabel={t("documentScanner.scannerBtn")}
                    onScanPress={() => {
                      empScanTargetIdRef.current = doc.id;
                      setEmpScannerOpen(true);
                    }}
                  />
                ))}
                {canAddMore ? (
                  catDocs.length === 0 ? (
                    <div className="grid grid-cols-1 gap-2">
                      <button type="button" onClick={() => { const newDoc = addEmpDoc(cat.key); setTimeout(() => empFileInputRefs.current[newDoc.id]?.click(), 50); }} className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer transition-colors"><i className="ri-file-upload-line text-sm" />{t("section7.uploadBtn")}</button>
                      <button
                        type="button"
                        onClick={() => {
                          const newDoc = addEmpDoc(cat.key);
                          empScanTargetIdRef.current = newDoc.id;
                          setEmpScannerOpen(true);
                        }}
                        className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer transition-colors"
                      >
                        <i className="ri-scan-line text-sm" />
                        {t("documentScanner.scannerBtn")}
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => addEmpDoc(cat.key)} className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer transition-colors"><i className="ri-add-line text-base" />{t("docUpload.empAddDoc")}</button>
                  )
                ) : null}
              </div>
            );
          })}

          <div className="bg-slate-50 rounded-2xl p-3 mb-3 mt-2">
            <p className="text-[13px] font-semibold text-slate-700 mb-2">{t("docUpload.empTipsTitle")}</p>
            <ul className="flex flex-col gap-1.5">
              {(["empTipOptional", "empTipVidaLaboral", "empTipJobOffer", "empTipInformal", "empTipAutonomo"] as const).map((key) => (
                <li key={key} className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-slate-500 text-sm mt-0.5 shrink-0" /><p className="text-[12px] text-slate-500 leading-relaxed">{t(`docUpload.${key}`)}</p></li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                cleanupEmptyDocs();
                if (hasEmploymentDocUpload) onContinueWithAck();
                else onSkipDone();
              }}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
            >
              {hasEmploymentDocUpload ? t("docFlow.continue") : t("docFlow.skip")}
            </button>
          </div>
        </>
      )}

      <DocumentDeleteConfirmModal
        open={empDeleteConfirmId !== null}
        busy={empDeleteBusy}
        onCancel={() => !empDeleteBusy && setEmpDeleteConfirmId(null)}
        onConfirm={() => void performEmpRemoveAfterConfirm()}
      />

      <MobileDocumentScanner
        open={empScannerOpen}
        onClose={() => {
          empScanTargetIdRef.current = null;
          setEmpScannerOpen(false);
        }}
        onComplete={async (pdf) => {
          const id = empScanTargetIdRef.current;
          if (id) await ingestEmpUpload(id, pdf);
          empScanTargetIdRef.current = null;
        }}
      />
    </div>
  );
}

interface AsylumUploadProps {
  onBack: () => void;
  onSkipDone: () => void;
  onContinueWithAck: () => void;
}

interface AsylumCategoryDef {
  key: string;
  labelKey: string;
  helperKey: string;
}

const ASYLUM_CATEGORIES: AsylumCategoryDef[] = [
  {
    key: "card",
    labelKey: "docUpload.asylumDocCard",
    helperKey: "docUpload.asylumDocCardHelper",
  },
  {
    key: "receipt",
    labelKey: "docUpload.asylumDocReceipt",
    helperKey: "docUpload.asylumDocReceiptHelper",
  },
  {
    key: "resolution",
    labelKey: "docUpload.asylumDocResolution",
    helperKey: "docUpload.asylumDocResolutionHelper",
  },
];

function mapAsylumCategoryToDocumentType(category: string): string {
  switch (category) {
    case "receipt":
      return "application_receipt_id";
    case "card":
      return "red_card_id";
    case "resolution":
      return "denial_resolution_id";
    default:
      return "asylum_section";
  }
}

interface AsylumDocCardProps {
  doc: AsylumDocument;
  onRemove: (id: string) => void;
  onFileChange: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: (el: HTMLInputElement | null) => void;
  t: (key: string) => string;
  onScanPress?: () => void;
  scannerBtnLabel?: string;
}

function AsylumDocCard({
  doc,
  onRemove,
  onFileChange,
  fileInputRef,
  t,
  onScanPress,
  scannerBtnLabel,
}: AsylumDocCardProps) {
  const inputId = `asylum-file-${doc.id}`;
  const scanSecondary = !!onScanPress;

  return (
            <div>
      {!doc.filename ? (
        <div className="grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={() => document.getElementById(inputId)?.click()}
            className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors"
          >
            <i className="ri-file-upload-line text-sm" />
            {t("section7.uploadBtn")}
          </button>
          <button
            type="button"
            onClick={() =>
              scanSecondary ? onScanPress?.() : document.getElementById(inputId)?.click()}
            className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors"
          >
            <i className="ri-scan-line text-sm" />
            {scannerBtnLabel || t("section6.cameraBtn")}
          </button>
            </div>
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <i className="ri-file-check-line text-emerald-600 text-base shrink-0" />
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <p className="text-[12px] font-medium text-emerald-800 truncate">{doc.filename}</p>
            {doc.fileSize ? (
              <span className="text-[11px] text-emerald-700 shrink-0">{doc.fileSize}</span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => document.getElementById(inputId)?.click()}
            className="text-[12px] text-emerald-700 underline cursor-pointer shrink-0 whitespace-nowrap"
          >
            {t("docUpload.modalChangeFile")}
          </button>
          <button
            type="button"
            onClick={() => onRemove(doc.id)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0"
            title={t("docUpload.removeDoc")}
          >
            <i className="ri-delete-bin-line text-base" />
          </button>
        </div>
      )}

          <input
        id={inputId}
        ref={fileInputRef}
            type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={(e) => onFileChange(doc.id, e)}
            className="hidden"
          />
        </div>
  );
}

function AsylumUpload({ onBack, onSkipDone, onContinueWithAck }: AsylumUploadProps) {
  const { t } = useLang();
  const { data, update, ensureCaseSession, caseSessionId } = useForm();
  const asylumFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const asylumScanTargetIdRef = useRef<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [asylumScannerOpen, setAsylumScannerOpen] = useState(false);
  const hasAsylumDocUpload = hasUploadedDocInRows(data.asylumDocuments);
  const [asylumDeleteConfirmId, setAsylumDeleteConfirmId] = useState<string | null>(null);
  const [asylumDeleteBusy, setAsylumDeleteBusy] = useState(false);

  const cleanupEmptyDocs = () => {
    const cleaned = data.asylumDocuments.filter((doc) => !!doc.filename);
    if (cleaned.length !== data.asylumDocuments.length) {
      update("asylumDocuments", cleaned);
    }
  };

  const docsForCategory = (cat: string) =>
    data.asylumDocuments.filter((doc) => doc.category === cat);

  const ensureAsylumDoc = (cat: string) => {
    const existingDoc = data.asylumDocuments.find((doc) => doc.category === cat);
    if (existingDoc) return existingDoc;

    const newDoc: AsylumDocument = {
      id: `asylum-${Date.now()}`,
      category: cat,
      filename: "",
      fileSize: "",
      description: "",
    };
    update("asylumDocuments", [...data.asylumDocuments, newDoc]);
    return newDoc;
  };

  const removeAsylumDoc = (id: string) => {
    update(
      "asylumDocuments",
      data.asylumDocuments.filter((doc) => doc.id !== id)
    );
  };

  const performAsylumRemoveAfterConfirm = async () => {
    const id = asylumDeleteConfirmId;
    if (!id) return;
    const doc = data.asylumDocuments.find((d) => d.id === id);
    if (!doc?.filename) {
      setAsylumDeleteConfirmId(null);
      return;
    }
    setAsylumDeleteBusy(true);
    setUploadError(null);
    try {
      const mid = resolveMongoDocumentIdForDelete(doc);
      const res = await deleteSessionUploadedFileIfApplicable({
        ensureCaseSession,
        caseSessionId,
        mongoDocumentId: mid,
      });
      if (res.ok === false) setUploadError(res.message);
      else removeAsylumDoc(id);
    } finally {
      setAsylumDeleteBusy(false);
      setAsylumDeleteConfirmId(null);
    }
  };

  const ingestAsylumUpload = async (id: string, file: File) => {
    const targetDoc = data.asylumDocuments.find((doc) => doc.id === id);
    if (!targetDoc) return;

    setUploadError(null);
    const result = await uploadCaseDocumentFile(
      ensureCaseSession,
      mapAsylumCategoryToDocumentType(targetDoc.category),
      file,
      {
        supportingDescription: targetDoc.description.trim() || undefined,
      }
    );

    if (result.ok === false) {
      setUploadError(result.message);
      if (asylumFileInputRefs.current[id]) {
        asylumFileInputRefs.current[id]!.value = "";
      }
      return;
    }

    update(
      "asylumDocuments",
      data.asylumDocuments.map((doc) =>
        doc.id === id
          ? {
              ...doc,
              filename: file.name,
              fileSize: formatStayFileSize(file.size),
              documentId: result.documentId ? String(result.documentId).trim() : doc.documentId,
            }
          : doc
      )
    );

    if (asylumFileInputRefs.current[id]) {
      asylumFileInputRefs.current[id]!.value = "";
    }
  };

  const handleAsylumFileChange = async (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await ingestAsylumUpload(id, file);
  };

  const renderAsylumSection = (category: AsylumCategoryDef) => {
    const categoryDoc = docsForCategory(category.key)[0];

    return (
      <div key={category.key} className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="mb-3">
          <p className="text-[15px] font-semibold text-slate-900 mb-1">{t(category.labelKey)}</p>
          <p className="text-[13px] text-slate-500 leading-relaxed">{t(category.helperKey)}</p>
        </div>

        {categoryDoc ? (
          <AsylumDocCard
            doc={categoryDoc}
            onRemove={(docId) => setAsylumDeleteConfirmId(docId)}
            onFileChange={handleAsylumFileChange}
            fileInputRef={(el) => {
              asylumFileInputRefs.current[categoryDoc.id] = el;
            }}
            t={t}
            scannerBtnLabel={t("documentScanner.scannerBtn")}
            onScanPress={() => {
              asylumScanTargetIdRef.current = categoryDoc.id;
              setAsylumScannerOpen(true);
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => {
                const newDoc = ensureAsylumDoc(category.key);
                setTimeout(() => asylumFileInputRefs.current[newDoc.id]?.click(), 0);
              }}
              className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors"
            >
              <i className="ri-file-upload-line text-sm" />
              {t("section7.uploadBtn")}
            </button>
            <button
              type="button"
              onClick={() => {
                const newDoc = ensureAsylumDoc(category.key);
                asylumScanTargetIdRef.current = newDoc.id;
                setAsylumScannerOpen(true);
              }}
              className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors"
            >
              <i className="ri-scan-line text-sm" />
              {t("documentScanner.scannerBtn")}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-6">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button
          type="button"
          onClick={() => {
            cleanupEmptyDocs();
            onBack();
          }}
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          {t("nav.breadcrumbOrganizer")}
        </button>
      </nav>

      <div className="mb-5">
        <h2 className="text-xl font-semibold text-slate-900 mb-1">{t("docUpload.asylumHeading")}</h2>
        <p className="text-[13px] text-slate-600 leading-relaxed">{t("docUpload.asylumDesc")}</p>
      </div>

      {uploadError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
          {uploadError}
        </p>
      ) : null}

      <div className="mb-5">
        <label htmlFor="oar-case-number" className="block text-[14px] font-semibold text-slate-900 mb-1.5">
          {t("section2e.oarLabel")}
        </label>
        <input
          id="oar-case-number"
          type="text"
          value={data.oarCaseNumber}
          onChange={(e) => update("oarCaseNumber", e.target.value)}
          placeholder={t("section2e.oarPlaceholder")}
          className="w-full h-12 px-4 bg-white border border-slate-200 rounded-2xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
        />
        <p className="text-[12px] text-slate-500 mt-2 leading-relaxed">{t("section2e.oarHelper")}</p>
          </div>

      <div className="space-y-3">
        {renderAsylumSection(ASYLUM_CATEGORIES[0])}

        <div className="text-center text-[13px] font-medium text-slate-400">
          {t("docUpload.or")}
          </div>

        {renderAsylumSection(ASYLUM_CATEGORIES[1])}

        <div className="text-center text-[13px] font-medium text-slate-400">
          {t("docUpload.or")}
        </div>

        {renderAsylumSection(ASYLUM_CATEGORIES[2])}
          </div>

      <div className="flex flex-col gap-2 mt-5">
        <button
          type="button"
          onClick={() => {
            cleanupEmptyDocs();
            if (hasAsylumDocUpload) onContinueWithAck();
            else onSkipDone();
          }}
          className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
        >
          {hasAsylumDocUpload ? t("docFlow.continue") : t("docFlow.skip")}
        </button>
          </div>

      <DocumentDeleteConfirmModal
        open={asylumDeleteConfirmId !== null}
        busy={asylumDeleteBusy}
        onCancel={() => !asylumDeleteBusy && setAsylumDeleteConfirmId(null)}
        onConfirm={() => void performAsylumRemoveAfterConfirm()}
      />

      <MobileDocumentScanner
        open={asylumScannerOpen}
        onClose={() => {
          asylumScanTargetIdRef.current = null;
          setAsylumScannerOpen(false);
        }}
        onComplete={async (pdf) => {
          const id = asylumScanTargetIdRef.current;
          if (id) await ingestAsylumUpload(id, pdf);
          asylumScanTargetIdRef.current = null;
        }}
      />
        </div>
  );
}

interface OtherSupportingDocumentsUploadProps {
  otherDocuments: OtherDocument[];
  update: (field: keyof import("../../context/FormContext").FormData, value: unknown) => void;
  ensureCaseSession: () => Promise<string | null>;
  caseSessionId: string | null;
  onBack: () => void;
  onSkipDone: () => void;
  onContinueWithAck: () => void;
  t: (key: string) => string;
}

function OtherSupportingDocumentsUpload({
  otherDocuments,
  update,
  ensureCaseSession,
  caseSessionId,
  onBack,
  onSkipDone,
  onContinueWithAck,
  t,
}: OtherSupportingDocumentsUploadProps) {
  const [selectedType, setSelectedType] = useState("");
  const [otrosDesc, setOtrosDesc] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [otherDeleteConfirmId, setOtherDeleteConfirmId] = useState<string | null>(null);
  const [otherDeleteBusy, setOtherDeleteBusy] = useState(false);

  const removeOtherDoc = (id: string) => {
    update(
      "otherDocuments",
      otherDocuments.filter((doc) => doc.id !== id)
    );
  };

  const performOtherRemoveAfterConfirm = async () => {
    const id = otherDeleteConfirmId;
    if (!id) return;
    const doc = otherDocuments.find((d) => d.id === id);
    if (!doc) {
      setOtherDeleteConfirmId(null);
      return;
    }
    setOtherDeleteBusy(true);
    setLocalError(null);
    try {
      const mid = resolveMongoDocumentIdForDelete(doc);
      const res = await deleteSessionUploadedFileIfApplicable({
        ensureCaseSession,
        caseSessionId,
        mongoDocumentId: mid,
      });
      if (res.ok === false) setLocalError(res.message);
      else removeOtherDoc(id);
    } finally {
      setOtherDeleteBusy(false);
      setOtherDeleteConfirmId(null);
    }
  };

  const supportingDescriptionForUpload = (): string => {
    if (!selectedType) return "";
    if (selectedType === "otros") return otrosDesc.trim();
    return OTHER_DOC_TYPES.find((x) => x.value === selectedType)?.label ?? selectedType;
  };

  const handleOtherFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedType) {
      setLocalError("Select a document type first.");
      e.target.value = "";
      return;
    }
    if (selectedType === "otros" && !otrosDesc.trim()) {
      setLocalError("Describe the document when «Otros» is selected.");
      e.target.value = "";
      return;
    }
    setLocalError(null);

    const result = await uploadCaseDocumentFile(
      ensureCaseSession,
      "other_supporting",
      file,
      {
        supportingDescription: supportingDescriptionForUpload(),
      }
    );

    if (result.ok === false) {
      setLocalError(result.message);
      e.target.value = "";
      return;
    }

    update("otherDocuments", [
      ...otherDocuments,
      {
        id: `other-${Date.now()}`,
        docType: selectedType,
        filename: file.name,
        fileSize: formatStayFileSize(file.size),
        description: selectedType === "otros" ? otrosDesc.trim() : "",
        documentId: result.documentId ? String(result.documentId).trim() : undefined,
      },
    ]);
    setSelectedType("");
    setOtrosDesc("");
    if (e.target) e.target.value = "";
  };

  const showOtrosField = selectedType === "otros";
  const canPickFile = Boolean(selectedType) && (!showOtrosField || Boolean(otrosDesc.trim()));
  const hasOtherDocUpload = hasUploadedDocInRows(otherDocuments);

  return (
    <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-6">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button
          type="button"
          onClick={() => onBack()}
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          {t("nav.breadcrumbOrganizer")}
        </button>
      </nav>

      <h2 className="text-xl font-semibold text-slate-900 mb-1">{t("docUpload.otherHeading")}</h2>
      <p className="text-[13px] text-slate-600 leading-relaxed mb-5">{t("docUpload.otherBody")}</p>

      <div className="mb-4 space-y-3">
        <div>
          <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
            {t("docUpload.documentTypeLabel")}
          </label>
          <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setOtrosDesc("");
              setLocalError(null);
            }}
            className={`w-full h-11 px-3 border border-slate-200 rounded-xl text-sm bg-white ${!selectedType ? "text-slate-400" : "text-slate-700"}`}
          >
            <option value="">{t("docUpload.documentTypePlaceholder")}</option>
            {OTHER_DOC_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
            </div>

        {showOtrosField ? (
            <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">
              {t("docUpload.otrosDescribeLabel")}
            </label>
            <textarea
              value={otrosDesc}
              onChange={(e) => setOtrosDesc(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 resize-y min-h-[80px]"
              placeholder={t("docUpload.otrosDescribePlaceholder")}
            />
            </div>
        ) : null}
          </div>

      {localError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
          {localError}
        </p>
            ) : null}

      {otherDocuments.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4">
          <div className="flex flex-col gap-2">
            {otherDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5"
              >
                <i className="ri-file-check-line text-emerald-600 text-base shrink-0" />
                <p className="flex-1 min-w-0 text-[12px] text-emerald-800 truncate">
                  <span className="font-medium">{doc.filename}</span>
                  <span className="text-emerald-700 font-normal">
                    {" · "}
                    {OTHER_DOC_TYPES.find((x) => x.value === doc.docType)?.label ?? doc.docType}
                    {doc.docType === "otros" && doc.description ? `: ${doc.description}` : ""}
                  </span>
                </p>
            <button
              type="button"
                  onClick={() => setOtherDeleteConfirmId(doc.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0"
                  title={t("docUpload.removeDoc")}
            >
                  <i className="ri-delete-bin-line text-base" />
            </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <label
        className={`w-full min-h-[44px] flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-dashed border-slate-300 rounded-xl font-medium text-[13px] transition-colors ${
          canPickFile ? "cursor-pointer" : "opacity-50 cursor-not-allowed pointer-events-none"
        }`}
      >
        <i className="ri-file-upload-line text-base" />
        {t("docUpload.otherAddDoc")}
            <input
              type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          onChange={handleOtherFilePick}
          disabled={!canPickFile}
              className="hidden"
            />
      </label>

      <p className="text-[11px] text-slate-500 mt-2 leading-snug">
        {t("docUpload.uploadRulesHint")}
      </p>

      <div className="flex flex-col gap-3 mt-8">
        <button
          type="button"
          onClick={() => {
            if (hasOtherDocUpload) onContinueWithAck();
            else onSkipDone();
          }}
          className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
        >
          {hasOtherDocUpload ? t("docFlow.continue") : t("docFlow.skip")}
        </button>
      </div>

      <DocumentDeleteConfirmModal
        open={otherDeleteConfirmId !== null}
        busy={otherDeleteBusy}
        onCancel={() => !otherDeleteBusy && setOtherDeleteConfirmId(null)}
        onConfirm={() => void performOtherRemoveAfterConfirm()}
      />
      </div>
    );
  }

/** Non-empty string trim helper for passport-ai extracted payloads. */
function strTrim(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * HTTP 200 with an empty or useless `extracted` object still looked like "success" to the UI,
 * so users saw no warning when the document was unreadable (e.g. blank scan). Require at least
 * one primary identifier before treating autofill as successful.
 */
function isIdentityAiExtractionMeaningful(
  extracted: Record<string, unknown>,
  identityDocumentType: string
): boolean {
  const dt = String(identityDocumentType || "").trim().toLowerCase().replace(/-/g, "_");
  const isPassport = dt === "passport" || dt === "passport_id" || dt === "passportid";

  if (isPassport) {
    return Boolean(strTrim(extracted.passport_number) || strTrim(extracted.full_name));
  }
  return Boolean(strTrim(extracted.document_number) || strTrim(extracted.passport_number));
}

type IdentityPassportAiOutcome = "idle" | "rejected";

export default function DocUpload({ category, onDone, onBack, onSaveAndFinishLater }: DocUploadProps) {
  const { t, tObj } = useLang();
  const { data, update, ensureCaseSession, caseSessionId } = useForm();
  const [docFlowUploadError, setDocFlowUploadError] = useState<string | null>(null);
  const [identityAiPhase, setIdentityAiPhase] = useState<"idle" | "uploading" | "analyzing" | "applying">("idle");
  const [identityProcessingPdf, setIdentityProcessingPdf] = useState(false);
  const [identityPassportAiOutcome, setIdentityPassportAiOutcome] = useState<IdentityPassportAiOutcome>("idle");
  const [showPhotoGuide, setShowPhotoGuide] = useState(false);
  const [showDocGuide, setShowDocGuide] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalUploading, setModalUploading] = useState(false);
  const [ocrFileName, setOcrFileName] = useState("");
  const [identityGateConfirmed, setIdentityGateConfirmed] = useState(false);
  const [identityGateChoice, setIdentityGateChoice] = useState<"" | "yes" | "no">("");
  /** User tapped Continue on Passport | Other ID (cases 1 + 2.1) so we do not re-show the gate while still on identity. */
  const passportIdentityGateDismissedRef = useRef(false);
  /** User entered via 2.2 / 2.3 (auto-skipped gate); keep them past the gate if data later matches “complete”. */
  const passportIdentityPastAutoSkipRef = useRef(false);
  /** Landing-page Skip should move to details form immediately without bouncing back to landing. */
  const passportLandingSkipToDetailsRef = useRef(false);
  /**
   * User confirmed "Other ID" on the Passport | Other gate this session — stay past the gate while
   * they fill the other-ID steps. Cleared when leaving identity or when returning with incomplete data
   * so they see the gate again (e.g. Back to Organizer then Identity).
   */
  const otherIdentityGateCommittedRef = useRef(false);
  const [identityReplaceConfirmOpen, setIdentityReplaceConfirmOpen] = useState(false);
  const sexOpts = tObj("section2.sexOptions");
  const sexOptions = Object.entries(sexOpts).map(([value, label]) => ({ value, label }));

  // Modal state
  const [modalDocType, setModalDocType] = useState("");
  const [modalFilename, setModalFilename] = useState("");
  const [expandedGroup, setExpandedGroup] = useState<"official" | "personal" | null>(null);
  const [otherDescription, setOtherDescription] = useState("");

  const fileRef = useRef<HTMLInputElement>(null);
  const ocrFileInputRef = useRef<HTMLInputElement>(null);
  const residenceFileRef = useRef<HTMLInputElement>(null);
  const [identityScannerOpen, setIdentityScannerOpen] = useState(false);
  const lastIdentityFileAttemptRef = useRef<File | null>(null);
  const [residenceScannerOpen, setResidenceScannerOpen] = useState(false);
  const [residenceDeleteConfirmIndex, setResidenceDeleteConfirmIndex] = useState<number | null>(null);
  const [residenceDeleteBusy, setResidenceDeleteBusy] = useState(false);
  const [denialDeleteOpen, setDenialDeleteOpen] = useState(false);
  const [denialDeleteBusy, setDenialDeleteBusy] = useState(false);
  const [vulnerabilityDeleteOpen, setVulnerabilityDeleteOpen] = useState(false);
  const [vulnerabilityDeleteBusy, setVulnerabilityDeleteBusy] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitModalSectionKey, setExitModalSectionKey] = useState<DocumentSectionAckKey | null>(null);
  const modalFileRef = useRef<HTMLInputElement>(null);
  const pendingModalFileRef = useRef<File | null>(null);

  /** In-app passport / identity file preview (blob URL); avoids ``window.open`` pop-up blockers. */
  const identityViewerBlobUrlRef = useRef<string | null>(null);
  const identityViewerLoadGenRef = useRef(0);
  const [identityDocViewerOpen, setIdentityDocViewerOpen] = useState(false);
  const [identityDocViewerUrl, setIdentityDocViewerUrl] = useState<string | null>(null);
  const [identityDocViewerMime, setIdentityDocViewerMime] = useState("");
  const [identityDocViewerLoading, setIdentityDocViewerLoading] = useState(false);
  const [identityDocViewerError, setIdentityDocViewerError] = useState<string | null>(null);

  const setStayDocs = useCallback(
    (docs: StayDocument[]) => update("stayDocuments", docs),
    [update]
  );
  useEffect(() => {
    if (category !== "identity") return;
    setIdentityPassportAiOutcome("idle");
  }, [category]);

  const passportAutoSkipIdentityGate =
    category === "identity" ? shouldAutoSkipPassportIdentityGate(data) : false;

  useEffect(() => {
    if (category !== "identity") {
      passportIdentityGateDismissedRef.current = false;
      passportIdentityPastAutoSkipRef.current = false;
      passportLandingSkipToDetailsRef.current = false;
      otherIdentityGateCommittedRef.current = false;
      return;
    }

    if (data.hasPassport === "no") {
      if (isIdentityCategoryComplete(data)) {
        setIdentityGateChoice("no");
        setIdentityGateConfirmed(true);
        return;
      }
      if (otherIdentityGateCommittedRef.current) {
        setIdentityGateChoice("no");
        setIdentityGateConfirmed(true);
        return;
      }
      setIdentityGateChoice("");
      setIdentityGateConfirmed(false);
      return;
    }

    if (data.hasPassport !== "yes") {
      setIdentityGateChoice("");
      setIdentityGateConfirmed(false);
      passportIdentityGateDismissedRef.current = false;
      passportIdentityPastAutoSkipRef.current = false;
      otherIdentityGateCommittedRef.current = false;
      return;
    }

    setIdentityGateChoice("yes");

    if (passportAutoSkipIdentityGate) {
      passportIdentityPastAutoSkipRef.current = true;
      passportIdentityGateDismissedRef.current = false;
      setIdentityGateConfirmed(true);
      return;
    }

    if (passportIdentityPastAutoSkipRef.current) {
      setIdentityGateConfirmed(true);
      return;
    }

    if (!passportIdentityGateDismissedRef.current) {
      setIdentityGateConfirmed(false);
    } else {
      setIdentityGateConfirmed(true);
    }
  }, [category, data, passportAutoSkipIdentityGate]);

  /** Restore OCR-phase filename when returning to identity after draft reload (local state only). */
  useEffect(() => {
    if (category !== "identity") return;
    /**
     * Do not keep upload-skip sticky across fresh Organizer re-entry:
     * if passport file is absent, reset OCR-skip so user sees identity upload flow again.
     */
    if (
      data.hasPassport === "yes" &&
      !hasPassportPdfUploaded(data) &&
      data.passportOcrSkipped === "yes" &&
      !passportLandingSkipToDetailsRef.current
    ) {
      update("passportOcrSkipped", "");
    }
    if (shouldShowPassportIdentityDetailForm(data)) return;
    if (data.hasPassport !== "yes") return;
    if (data.passportOcrSkipped === "yes") return;
    const saved = strTrim(data.identityUploads?.passport);
    if (!saved) return;
    setOcrFileName((prev) => (prev ? prev : saved));
  }, [category, data]);

  useEffect(() => {
    if (category !== "residence") return;
    const existing = data.stayDocuments || [];
    if (!existing.length) {
      setUploadedDocs([]);
      return;
    }
    const docTypeFromType: Record<string, string> = {
      padron_municipal_registration_id: "Padron",
      rental_contract_id: "Rental",
      medical_record_id: "Medical",
      bank_statement_id: "Bank",
      utility_bill_id: "Utility",
      payslip_work_contract_id: "Payslip",
      childrens_school_enrollment_id: "School",
      money_transfer_receipt_id: "Transfer",
      course_training_certificate_id: "Course",
      transport_ticket_id: "Transport",
      police_report_id: "Police",
      official_letter_notification_id: "Official",
      other: "Other",
    };
    const hydrated: UploadedDoc[] = existing.map((d) => ({
      id: d.id,
      filename: d.filename || "",
      /** Summary merge stores tier keys (`Padron`, `Course`, …); local rows may still use API `*_id` strings. */
      docType: (docTypeFromType[d.type] ?? d.type) || "Other",
      description: d.description,
    }));
    setUploadedDocs(hydrated);
  }, [category, data.stayDocuments]);
  const stayListUpload = useCallback(
    async (docId: string, file: File, tierKey: string) => {
      setDocFlowUploadError(null);
      const documentType = mapStayTierKeyToDocumentType(tierKey);
      return uploadCaseDocumentFile(ensureCaseSession, documentType, file);
    },
    [ensureCaseSession]
  );

  /** Checklist "complete" only after exit modal "Mark complete"; sync to Mongo `document_section_acknowledged`. */
  const openDocumentSectionExitModal = useCallback((key: DocumentSectionAckKey) => {
    setExitModalSectionKey(key);
    setShowExitModal(true);
  }, []);

  const finalizeDocumentSectionAfterModalYes = useCallback(async () => {
    const key = exitModalSectionKey;
    if (!key) {
      setShowExitModal(false);
      return;
    }
    update("documentSectionAcknowledged", { [key]: true });
    setShowExitModal(false);
    setExitModalSectionKey(null);
    try {
      const sid = await ensureCaseSession();
      if (sid) await mvpApi.acknowledgeDocumentSection(sid, key);
    } catch {
      // Local state + draft still apply; server sync retries on next session load.
    }
    onDone();
  }, [exitModalSectionKey, update, ensureCaseSession, onDone]);

  const handleSectionExitComeBackLater = useCallback(async () => {
    const key = exitModalSectionKey;
    setShowExitModal(false);
    setExitModalSectionKey(null);
    if (!key) return;

    update("documentSectionAcknowledged", { [key]: false });

    try {
      const sid = await ensureCaseSession();
      if (sid) {
        await mvpApi.unacknowledgeDocumentSection(sid, key);
      }
    } catch {
      // Local flags still drive checklist; server sync can retry on reload.
    }

    if (onSaveAndFinishLater) {
      onSaveAndFinishLater(key);
    } else {
      onBack();
    }
  }, [exitModalSectionKey, update, ensureCaseSession, onSaveAndFinishLater, onBack]);

  const sectionExitModalEl =
    showExitModal && exitModalSectionKey ? (
      <SectionExitModal
        sectionKey={exitModalSectionKey}
        onClose={() => {
          setShowExitModal(false);
          setExitModalSectionKey(null);
        }}
        onComeBackLater={handleSectionExitComeBackLater}
        onAdvance={finalizeDocumentSectionAfterModalYes}
      />
    ) : null;

  const mapIdentityDocTypeToUploadType = (rawDocType: string): string => {
    const dt = String(rawDocType || "").trim();
    if (!dt) return "passport_id";
    const key = dt.toLowerCase();
    if (key === "passport") return "passport_id";
    if (key === "nationalid") return "national_id_card_id";
    if (key === "traveldoc") return "travel_document_id";
    if (key === "cedulainscripcion") return "cedula_de_inscripcion_id";
    if (key === "other") return "other_id";
    return key;
  };

  const getIdentityUploadFilename = (docType: typeof data.docType): string => {
    if (!docType) return "";
    return data.identityUploads?.[docType] || "";
  };

  const revokeIdentityViewerBlob = useCallback(() => {
    const u = identityViewerBlobUrlRef.current;
    if (u) {
      URL.revokeObjectURL(u);
      identityViewerBlobUrlRef.current = null;
    }
    setIdentityDocViewerUrl(null);
    setIdentityDocViewerMime("");
  }, []);

  const closeIdentityDocumentViewer = useCallback(() => {
    identityViewerLoadGenRef.current += 1;
    setIdentityDocViewerOpen(false);
    setIdentityDocViewerLoading(false);
    setIdentityDocViewerError(null);
    revokeIdentityViewerBlob();
  }, [revokeIdentityViewerBlob]);

  useEffect(() => {
    return () => {
      const u = identityViewerBlobUrlRef.current;
      if (u) URL.revokeObjectURL(u);
    };
  }, []);

  useEffect(() => {
    if (!identityDocViewerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (identityDocViewerLoading) return;
      closeIdentityDocumentViewer();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [identityDocViewerOpen, identityDocViewerLoading, closeIdentityDocumentViewer]);

  const openIdentityDocumentViewer = useCallback(async () => {
    const loadGen = ++identityViewerLoadGenRef.current;
    const docId = String(data.identityPassportDocumentId ?? "").trim();
    const filename = getIdentityUploadFilename(data.docType);
    revokeIdentityViewerBlob();
    setIdentityDocViewerError(null);
    setIdentityDocViewerOpen(true);
    setIdentityDocViewerLoading(true);
    try {
      let blob: Blob | null = null;
      if (docId) {
        const sid = caseSessionId || (await ensureCaseSession());
        if (!sid) throw new Error("no_session");
        const res = await mvpApi.downloadSessionDocumentFile(sid, docId);
        blob = res.data as Blob;
      } else if (lastIdentityFileAttemptRef.current) {
        blob = lastIdentityFileAttemptRef.current;
      }
      if (loadGen !== identityViewerLoadGenRef.current) return;
      if (!blob || !(blob instanceof Blob) || blob.size === 0) throw new Error("empty");
      const lower = filename.toLowerCase();
      const mime =
        blob.type ||
        (lower.endsWith(".pdf") ? "application/pdf" : lower.match(/\.(jpe?g|png|webp|heic)$/i) ? "image/jpeg" : "application/octet-stream");
      const url = URL.createObjectURL(blob);
      identityViewerBlobUrlRef.current = url;
      setIdentityDocViewerUrl(url);
      setIdentityDocViewerMime(mime);
    } catch {
      if (loadGen === identityViewerLoadGenRef.current) {
        setIdentityDocViewerError(t("docUpload.passportViewUnavailable"));
      }
    } finally {
      if (loadGen === identityViewerLoadGenRef.current) {
        setIdentityDocViewerLoading(false);
      }
    }
  }, [
    caseSessionId,
    data.docType,
    data.identityUploads,
    data.identityPassportDocumentId,
    ensureCaseSession,
    revokeIdentityViewerBlob,
    t,
  ]);

  const getSelectedIdentityDocumentNumber = (docType: typeof data.docType): string => {
    switch (docType) {
      case "passport":
        return data.passportNumber;
      case "nationalId":
        return data.nationalIdNumber;
      case "travelDoc":
        return data.travelDocumentNumber;
      case "cedulaInscripcion":
        return data.registrationDocumentNumber;
      case "other":
        return data.otherDocumentNumber;
      default:
        return "";
    }
  };

  const updateSelectedIdentityDocumentNumber = (docType: typeof data.docType, value: string) => {
    switch (docType) {
      case "passport":
        update("passportNumber", value);
        break;
      case "nationalId":
        update("nationalIdNumber", value);
        break;
      case "travelDoc":
        update("travelDocumentNumber", value);
        break;
      case "cedulaInscripcion":
        update("registrationDocumentNumber", value);
        break;
      case "other":
        update("otherDocumentNumber", value);
        break;
      default:
        break;
    }
  };

  const applyIdentityDocTypeSelection = (nextDocType: typeof data.docType) => {
    const nextUpload = getIdentityUploadFilename(nextDocType);
    update("docType", nextDocType);
    update("docUpload", nextUpload);
  };

  const ingestIdentityFile = async (file: File) => {
    const pre = validateClientDocumentFile(file);
    if (pre.ok === false) {
      lastIdentityFileAttemptRef.current = null;
      setDocFlowUploadError(pre.message);
      return;
    }
    setDocFlowUploadError(null);
    setIdentityPassportAiOutcome("idle");
    lastIdentityFileAttemptRef.current = file;
    const isPdf = file.type === "application/pdf";
    setIdentityProcessingPdf(isPdf);
    setOcrFileName(file.name);
    setIdentityAiPhase("uploading");
    const identityDocumentType = String(data.docType || "passport").trim() || "passport";
    const identityUploadKey = identityDocumentType as keyof typeof data.identityUploads;
    update("docUpload" as Parameters<typeof update>[0], file.name);
    update("identityUploads", {
      ...data.identityUploads,
      [identityUploadKey]: file.name,
    });
    const identityUploadType = mapIdentityDocTypeToUploadType(identityDocumentType);
    const r = await uploadCaseDocumentFileWithRetry(ensureCaseSession, identityUploadType, file);
    if (r.ok === false) {
      setDocFlowUploadError(r.message);
      setIdentityPassportAiOutcome("rejected");
      setIdentityAiPhase("idle");
      setIdentityProcessingPdf(false);
      setOcrFileName("");
      update("docUpload" as Parameters<typeof update>[0], "");
      update("identityUploads", {
        ...data.identityUploads,
        [identityUploadKey]: "",
      });
      if (identityUploadKey === "passport") {
        update("identityPassportDocumentId", undefined);
      }
      lastIdentityFileAttemptRef.current = null;
      return;
    }

    if (identityDocumentType === "passport" && r.documentId) {
      update("identityPassportDocumentId", r.documentId);
    }

    const shouldAdvanceToPassportForm = identityDocumentType === "passport";

    let extractionMeaningful = false;

    try {
      setIdentityAiPhase("analyzing");
      const aiRes = await mvpApi.analyzePassportDocument(file, identityDocumentType);
      const extracted = aiRes?.data?.extracted || {};
      setIdentityAiPhase("applying");

      extractionMeaningful = isIdentityAiExtractionMeaningful(
        extracted as Record<string, unknown>,
        identityDocumentType
      );

      if (extractionMeaningful) {
        const applyExtractedDob = () => {
          if (typeof extracted.date_of_birth === "string") {
            const [year = "", month = "", day = ""] = extracted.date_of_birth.split("-");
            if (year && month && day) {
              update("dob", { day, month, year });
            }
          }
        };
        const applyExtractedBirthFields = () => {
          if (typeof extracted.sex === "string" && extracted.sex.trim()) {
            const normalizedSex = extracted.sex.trim().toLowerCase();
            if (normalizedSex === "male" || normalizedSex === "female" || normalizedSex === "undetermined") {
              update("sex", normalizedSex as typeof data.sex);
            }
          }
          if (typeof extracted.sex === "string" && extracted.sex.trim()) {
            const normalizedSex = extracted.sex.trim().toLowerCase();
            if (normalizedSex === "male" || normalizedSex === "female" || normalizedSex === "undetermined") {
              update("sex", normalizedSex as typeof data.sex);
            }
          }
          update("countryOfBirth", strTrim(extracted.country_of_birth));
          update("placeOfBirth", strTrim(extracted.place_of_birth));
        };

        if (identityDocumentType === "passport") {
          if (typeof extracted.full_name === "string" && extracted.full_name.trim()) {
            update("docFullName", extracted.full_name.trim());
          }
          if (typeof extracted.passport_number === "string" && extracted.passport_number.trim()) {
            update("passportNumber", extracted.passport_number.trim().toUpperCase().replace(/[^A-Z0-9]/g, ""));
          }
          if (typeof extracted.expiry_date === "string") {
            const [year = "", month = "", day = ""] = extracted.expiry_date.split("-");
            if (year && month && day) {
              update("docExpiry", { day, month, year });
            }
          }
          applyExtractedDob();
          applyExtractedBirthFields();
        } else {
          if (typeof extracted.document_number === "string" && extracted.document_number.trim()) {
            updateSelectedIdentityDocumentNumber(
              identityDocumentType as typeof data.docType,
              extracted.document_number.trim()
            );
          } else if (typeof extracted.passport_number === "string" && extracted.passport_number.trim()) {
            updateSelectedIdentityDocumentNumber(
              identityDocumentType as typeof data.docType,
              extracted.passport_number.trim()
            );
          }
          if (typeof extracted.issuing_country === "string" && extracted.issuing_country.trim()) {
            update("nationalIdCountry", extracted.issuing_country.trim());
          }
          applyExtractedDob();
          applyExtractedBirthFields();
        }
      }
    } catch (err) {
      console.warn("Identity AI analyze failed", err);
      extractionMeaningful = false;
    } finally {
      setIdentityAiPhase("idle");
      setIdentityProcessingPdf(false);
    }

    if (shouldAdvanceToPassportForm) {
      update("passportOcrSkipped", "yes");
      setOcrFileName("");
      lastIdentityFileAttemptRef.current = null;
      return;
    }

    lastIdentityFileAttemptRef.current = null;
  };

  const handleIdentityFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await ingestIdentityFile(file);
    if (e.target) e.target.value = "";
  };

  const replaceIdentityUploadedFile = useCallback(async (): Promise<boolean> => {
    if (identityAiPhase !== "idle") return false;
    const deleteResult = await deleteSessionUploadedFileIfApplicable({
      ensureCaseSession,
      caseSessionId,
      mongoDocumentId: resolveMongoDocumentIdForDelete({
        documentId: data.identityPassportDocumentId,
      }),
    });
    if (!deleteResult.ok) {
      const deleteMessage = "message" in deleteResult ? deleteResult.message : "Could not delete the file. Please try again.";
      setDocFlowUploadError(deleteMessage);
      return false;
    }
    setIdentityPassportAiOutcome("idle");
    setDocFlowUploadError(null);
    lastIdentityFileAttemptRef.current = null;
    setOcrFileName("");
    const docKey = data.docType || "passport";
    update("identityUploads", {
      ...(data.identityUploads ?? {}),
      [docKey]: "",
    });
    update("docUpload", "");
    update("identityPassportDocumentId", undefined);
    if (data.hasPassport === "yes") {
      update("passportOcrSkipped", "");
    }
    return true;
  }, [
    caseSessionId,
    data.docType,
    data.hasPassport,
    data.identityPassportDocumentId,
    data.identityUploads,
    ensureCaseSession,
    identityAiPhase,
    update,
  ]);

  const confirmReplaceIdentityAndReturnToGate = useCallback(async () => {
    if (identityAiPhase !== "idle") return;
    setIdentityReplaceConfirmOpen(false);
    setIdentityScannerOpen(false);
    const replaced = await replaceIdentityUploadedFile();
    if (!replaced) return;
    passportIdentityGateDismissedRef.current = false;
    passportIdentityPastAutoSkipRef.current = false;
    otherIdentityGateCommittedRef.current = false;
    setIdentityGateConfirmed(false);
    if (data.hasPassport === "yes") setIdentityGateChoice("yes");
    else if (data.hasPassport === "no") setIdentityGateChoice("no");
    else setIdentityGateChoice("");
  }, [data.hasPassport, identityAiPhase, replaceIdentityUploadedFile]);

  const arrivalYear = data.arrivalDate.year;
  const arrivalMonth = data.arrivalDate.month;

  // ── Identity — combined briefing + upload ──
  if (category === "identity") {
    const noPassport = data.hasPassport === "no";
    const value = getIdentityUploadFilename(data.docType);
    const isIdentityAiBusy = identityAiPhase !== "idle";
    const identityGateShowHint = !(data.hasPassport === "yes" || data.hasPassport === "no");

    let identityBusyTitle = t("docUpload.identityAiUploadingSecure");
    let identityBusySub = t("docUpload.identityAiWaitHint");
    if (identityProcessingPdf && isIdentityAiBusy) {
      identityBusyTitle = t("docUpload.passportPdfProcessingTitle");
      if (identityAiPhase === "uploading") identityBusySub = t("docUpload.passportPdfPhaseUpload");
      else if (identityAiPhase === "analyzing") identityBusySub = t("docUpload.passportPdfPhaseAnalyze");
      else if (identityAiPhase === "applying") identityBusySub = t("docUpload.passportPdfPhaseApply");
    }

    return (
      <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-10 min-h-[100dvh] bg-[#faf9f6]">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button
          type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
        >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            {t("nav.breadcrumbOrganizer")}
        </button>
        </nav>

        {docFlowUploadError && (
          <div className="rounded-lg border border-red-100 bg-red-50/90 px-3 py-3 mb-4">
            <p className="text-sm text-red-700">{docFlowUploadError}</p>
            {identityPassportAiOutcome === "rejected" &&
            !isIdentityAiBusy &&
            identityGateConfirmed &&
            data.hasPassport === "yes" &&
            data.passportOcrSkipped !== "yes" ? (
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setDocFlowUploadError(null);
                    setIdentityPassportAiOutcome("idle");
                    const f = lastIdentityFileAttemptRef.current;
                    if (f) void ingestIdentityFile(f);
                    else ocrFileInputRef.current?.click();
                  }}
                  className="flex-1 min-h-[44px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-semibold cursor-pointer px-3"
                >
                  {t("docUpload.identityUploadRetry")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    lastIdentityFileAttemptRef.current = null;
                    setDocFlowUploadError(null);
                    setIdentityPassportAiOutcome("idle");
                    ocrFileInputRef.current?.click();
                  }}
                  className="flex-1 min-h-[44px] rounded-xl border border-red-200 bg-white hover:bg-red-50 text-red-900 text-[14px] font-semibold cursor-pointer px-3"
                >
                  {t("docUpload.identityUploadPickAnother")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {!identityGateConfirmed && (
          <div className="mb-8">
            <p className="text-[14px] font-medium text-slate-800 mb-3">{t("docUpload.identityGateQuestion")}</p>
            {identityGateShowHint ? (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-3 flex items-start gap-3">
                <i className="ri-information-line text-emerald-600 text-base mt-0.5 shrink-0" />
                <p className="text-[13px] text-emerald-800 leading-relaxed">{t("docUpload.identityGateHint")}</p>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setIdentityGateChoice("yes")}
                className={`min-w-0 flex flex-col items-center justify-center min-h-[120px] p-4 bg-white border rounded-2xl transition-all cursor-pointer touch-manipulation ${
                  identityGateChoice === "yes"
                    ? "border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-50/80"
                    : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50"
                }`}
              >
                <span className="text-[13px] font-semibold text-slate-800 text-center">{t("docUpload.identityGatePassport")}</span>
              </button>
              <button
                type="button"
                onClick={() => setIdentityGateChoice("no")}
                className={`min-w-0 flex flex-col items-center justify-center min-h-[120px] p-4 bg-white border rounded-2xl transition-all cursor-pointer touch-manipulation ${
                  identityGateChoice === "no"
                    ? "border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-50/80"
                    : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50"
                }`}
              >
                <span className="text-[13px] font-semibold text-slate-800 text-center">{t("docUpload.identityGateOther")}</span>
              </button>
            </div>
            <button
              type="button"
              disabled={!identityGateChoice}
              onClick={() => {
                if (identityGateChoice === "yes") {
                  otherIdentityGateCommittedRef.current = false;
                  passportIdentityGateDismissedRef.current = true;
                  update("hasPassport", "yes");
                  update("passportOcrSkipped", "");
                  applyIdentityDocTypeSelection("passport");
                } else if (identityGateChoice === "no") {
                  otherIdentityGateCommittedRef.current = true;
                  update("hasPassport", "no");
                  update("passportOcrSkipped", "yes");
                  applyIdentityDocTypeSelection("");
                  update("docUpload", "");
                }
                setIdentityGateConfirmed(true);
              }}
              className="mt-6 w-full min-h-[52px] rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-45 disabled:cursor-not-allowed text-white text-[15px] font-semibold transition-colors touch-manipulation"
            >
              {t("docUpload.identityGateContinue")}
            </button>
            {identityGateChoice === "yes" ? (
              <button
                type="button"
                onClick={() => {
                  otherIdentityGateCommittedRef.current = false;
                  passportIdentityGateDismissedRef.current = true;
                  update("hasPassport", "yes");
                  update("passportOcrSkipped", "yes");
                  applyIdentityDocTypeSelection("passport");
                  setIdentityGateConfirmed(true);
                }}
                className="mt-3 w-full min-h-[48px] rounded-xl border border-slate-300 bg-white text-slate-800 text-[15px] font-semibold hover:bg-slate-50 transition-colors touch-manipulation"
              >
                {t("docFlow.skip")}
              </button>
            ) : null}
            </div>
        )}

        {identityGateConfirmed && shouldShowPassportUploadLanding(data) && (
          <div className="mb-8">
            {isIdentityAiBusy ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-5 py-12 flex flex-col items-center justify-center gap-3 text-center shadow-sm">
                <i className="ri-loader-4-line text-4xl text-emerald-600 animate-spin shrink-0" aria-hidden="true" />
                <p className="text-[17px] font-semibold text-slate-900">{identityBusyTitle}</p>
                <p className="text-[13px] text-slate-600 max-w-sm leading-snug">{identityBusySub}</p>
          </div>
            ) : (
              <>
                <input
                  ref={ocrFileInputRef}
                  type="file"
                  accept={DOCUMENT_FILE_ACCEPT}
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    await handleIdentityFile(e);
                    if (ocrFileInputRef.current) ocrFileInputRef.current.value = "";
                  }}
                />

                    {!ocrFileName ? (
                      <>
                        <p className="text-[13px] font-medium text-slate-500 mb-2">{t("docFlow.catIdentity")}</p>
                        <h2 className="text-[clamp(1.6rem,5vw,1.85rem)] font-bold text-slate-900 tracking-tight leading-[1.15] mb-4">
                          {t("docUpload.passportAddTitle")}
                        </h2>
                        <p className="text-[15px] text-slate-600 leading-relaxed mb-3">{t("docUpload.passportAddBody1")}</p>
                        <p className="text-[15px] text-slate-600 leading-relaxed mb-5">{t("docUpload.passportAddBody2")}</p>

                        <button
                          type="button"
                          onClick={() => setShowPhotoGuide(true)}
                          className="flex items-start gap-3 text-left mb-8 w-full bg-transparent border-0 p-0 cursor-pointer group touch-manipulation"
                        >
                          <span
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm"
                            aria-hidden="true"
                          >
                            <i className="ri-information-fill text-[20px]" />
                          </span>
                          <span className="text-[15px] font-medium text-emerald-700 underline underline-offset-[3px] decoration-emerald-600/90 group-hover:text-emerald-800 pt-1 leading-snug">
                            {t("docUpload.passportAddExamplesLink")}
                          </span>
                        </button>

                        <div className="flex flex-col gap-4 mb-8">
                          <button
                            type="button"
                            onClick={() => setIdentityScannerOpen(true)}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-5 text-left shadow-sm hover:bg-slate-50/90 transition-colors touch-manipulation"
                          >
                            <p className="text-[16px] font-bold text-slate-900">{t("docUpload.passportAddScannerCardTitle")}</p>
                            <p className="text-[14px] text-slate-600 mt-2 leading-snug">
                              {t("docUpload.passportAddScannerCardDesc")}
                            </p>
                          </button>

                          <button
                            type="button"
                            onClick={() => ocrFileInputRef.current?.click()}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-5 text-left shadow-sm hover:bg-slate-50/90 transition-colors touch-manipulation"
                          >
                            <p className="text-[16px] font-bold text-slate-900">{t("docUpload.passportAddPdfCardTitle")}</p>
                            <p className="text-[14px] text-slate-600 mt-2 leading-snug">
                              {t("docUpload.passportAddPdfCardDesc")}
                            </p>
                          </button>
        </div>

                        <button
                          type="button"
                          onClick={() => {
                            passportLandingSkipToDetailsRef.current = true;
                            update("passportOcrSkipped", "yes");
                            setIdentityPassportAiOutcome("idle");
                            setIdentityGateConfirmed(true);
                          }}
                          className="w-full min-h-[52px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 transition-colors touch-manipulation"
                        >
                          {t("docFlow.skip")}
                        </button>
                      </>
                    ) : null}

                    {ocrFileName ? (
                      <div className="mb-6">
                        <div
                          className={`flex items-center gap-3 px-4 py-4 rounded-2xl border-2 ${
                            isIdentityAiBusy ? "bg-white border-slate-200" : "bg-emerald-50 border-emerald-300"
                          }`}
                        >
                          {isIdentityAiBusy ? (
                            <i className="ri-loader-4-line text-2xl text-emerald-600 animate-spin shrink-0" aria-hidden="true" />
                          ) : (
                            <i className="ri-checkbox-circle-fill text-2xl text-emerald-600 shrink-0" aria-hidden="true" />
                          )}
                          <div className="flex-1 min-w-0">
                            {!isIdentityAiBusy ? (
                              <p className="text-[13px] font-semibold mb-0.5 text-emerald-800">{t("docUpload.ocrSuccessTitle")}</p>
                            ) : null}
                            <span className="text-[12px] text-slate-600 truncate block">{ocrFileName}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}

              </>
            )}
          </div>
        )}

        {identityGateConfirmed &&
          (data.hasPassport === "no" || shouldShowPassportIdentityDetailForm(data)) && (
          <>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              {t("docUpload.identityStep1")}
            </p>

            {value ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 mb-6 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-emerald-600 shrink-0" aria-hidden="true" />
                  <span className="flex-1 min-w-0 truncate text-[14px] font-medium text-slate-800">{value}</span>
                  <div className="flex flex-wrap items-center gap-2 shrink-0 ml-auto">
                    {data.docType === "passport" && data.hasPassport === "yes" ? (
                      <button
                        type="button"
                        disabled={isIdentityAiBusy}
                        onClick={() => void openIdentityDocumentViewer()}
                        className="rounded-lg px-2 py-1.5 text-[14px] font-semibold text-emerald-700 underline underline-offset-[3px] hover:bg-emerald-100/80 hover:text-emerald-800 disabled:opacity-45 disabled:pointer-events-none touch-manipulation"
                      >
                        {t("docUpload.passportScanSavedView")}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={isIdentityAiBusy}
                      onClick={() => setIdentityReplaceConfirmOpen(true)}
                      className="rounded-lg px-2 py-1.5 text-[14px] font-semibold text-emerald-700 underline underline-offset-[3px] hover:bg-emerald-100/80 hover:text-emerald-800 shrink-0 disabled:opacity-45 disabled:pointer-events-none touch-manipulation"
                    >
                      {t("docUpload.passportScanSavedReplace")}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {noPassport ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-8">
                <div className="flex flex-col gap-4">
            <div>
                    <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">
                      {t("docUpload.nationalIdType")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { v: "nationalId", l: t("docUpload.typeNationalId") },
                        { v: "travelDoc", l: t("docUpload.typeTravelDoc") },
                        { v: "cedulaInscripcion", l: t("docUpload.typeCedulaInscripcion") },
                        { v: "other", l: t("docUpload.typeOther") },
                      ].map((option) => (
          <button
                          key={option.v}
                          type="button"
                          onClick={() => {
                            applyIdentityDocTypeSelection(option.v as typeof data.docType);
                          }}
                          className={`max-w-full px-3 py-2 rounded-lg border text-[13px] font-medium transition-all text-center break-words ${
                            data.docType === option.v
                              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 cursor-pointer"
                          }`}
                        >
                          {option.l}
          </button>
                      ))}
          </div>
          </div>

                  <div>
                    <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">
                      {t("docUpload.nationalIdNumber")}
                    </label>
          <input
                      type="text"
                      value={getSelectedIdentityDocumentNumber(data.docType)}
                      onChange={(e) => updateSelectedIdentityDocumentNumber(data.docType, e.target.value)}
                      placeholder="1234567890"
                      className="w-full h-12 px-3 bg-white border border-slate-200 rounded-xl text-[16px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300"
          />
        </div>

                  <div>
                    <DateInput
                      field="dob"
                      label={t("section2.dob")}
                      helper={t("section2.dobHelper")}
                      disallowFuture={true}
                      skipYearRange
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
                      {t("section2.placeOfBirth")}
                    </label>
                    <input
                      type="text"
                      value={data.placeOfBirth}
                      onChange={(e) => update("placeOfBirth", e.target.value)}
                      placeholder={t("section2.placeOfBirthPlaceholder")}
                      className="w-full h-12 px-3 bg-white border border-slate-200 rounded-xl text-[16px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                    />
                  </div>

                  <SearchableDropdown
                    fieldId="field-countryOfBirth-no-passport"
                    label={t("section2.countryOfBirth")}
                    options={COUNTRIES}
                    value={data.countryOfBirth}
                    onChange={(v) => update("countryOfBirth", v)}
                    placeholder={t("section2.countryOfBirthPlaceholder")}
                  />

                  <ButtonChoice
                    fieldId="field-sex-no-passport"
                    label={t("section2.sex")}
                    options={sexOptions}
                    value={data.sex}
                    onChange={(v) => update("sex", v as typeof data.sex)}
                  />
        </div>
          </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-8">
                <div className="flex flex-col gap-4">
          <div>
                    <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
                      {t("docUpload.passportNumber")}
                    </label>
                    <input
                      type="text"
                      value={data.passportNumber}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                        update("passportNumber", val);
                      }}
                      placeholder="AB1234567"
                      style={{ fontSize: "16px" }}
                      className={`w-full h-12 px-4 bg-white border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${
                        data.passportNumber ? "border-emerald-400" : "border-slate-200"
                      }`}
                    />
          </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
                      {t("docUpload.passportFullName")}
                    </label>
                    <input
                      type="text"
                      value={data.docFullName}
                      onChange={(e) => {
                        update("docFullName", e.target.value.toUpperCase());
                      }}
                      placeholder="GARCÍA LÓPEZ, MARÍA"
                      maxLength={120}
                      style={{ fontSize: "16px" }}
                      className={`w-full h-12 px-4 bg-white border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${
                        data.docFullName ? "border-emerald-400" : "border-slate-200"
                      }`}
                    />
        </div>

                  <div>
                    <DateInput
                      field="dob"
                      label={t("section2.dob")}
                      helper={t("section2.dobHelper")}
                      disallowFuture={true}
                      skipYearRange
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
                      {t("section2.placeOfBirth")}
                    </label>
                    <input
                      type="text"
                      value={data.placeOfBirth}
                      onChange={(e) => update("placeOfBirth", e.target.value)}
                      placeholder={t("section2.placeOfBirthPlaceholder")}
                      className="w-full h-12 px-3 bg-white border border-slate-200 rounded-xl text-[16px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                    />
                  </div>

                  <SearchableDropdown
                    fieldId="field-countryOfBirth-passport"
                    label={t("section2.countryOfBirth")}
                    options={COUNTRIES}
                    value={data.countryOfBirth}
                    onChange={(v) => update("countryOfBirth", v)}
                    placeholder={t("section2.countryOfBirthPlaceholder")}
                  />

                  <ButtonChoice
                    fieldId="field-sex-passport"
                    label={t("section2.sex")}
                    options={sexOptions}
                    value={data.sex}
                    onChange={(v) => update("sex", v as typeof data.sex)}
                  />

          </div>
              </div>
            )}

            {!value && noPassport ? (
              <>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  {noPassport ? t("docUpload.identityStep2NoPassport") : t("docUpload.identityStep2")}
                </p>

                <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
                  {noPassport ? t("docUpload.identityStep2BodyNoPassport") : t("docUpload.identityStep2Body")}
                </p>

                {!noPassport && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-3 mb-3 flex flex-col gap-1.5">
                    {["identityTip1", "identityTip4", "identityTip3"].map((key) => (
                      <div key={key} className="flex items-start gap-2.5">
                        <i className="ri-check-line text-emerald-500 text-sm mt-0.5 shrink-0" />
                        <p className="text-[13px] text-slate-600">{t(`docBriefing.${key}`)}</p>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => setShowPhotoGuide(true)}
                      className="flex items-center gap-1.5 text-[13px] text-emerald-600 font-semibold cursor-pointer hover:text-emerald-700 mt-0.5 transition-colors"
                    >
                      <i className="ri-image-line text-sm" />
                      {t("section6.seeExample")}
                    </button>
                  </div>
                )}

                <div className="flex flex-col gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={isIdentityAiBusy}
                    className="w-full min-w-0 min-h-[44px] flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[14px] text-center leading-tight cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-file-upload-line text-base" />
                    <span className="min-w-0 break-words">{t("docUpload.chooseFile")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIdentityScannerOpen(true)}
                    disabled={isIdentityAiBusy}
                    className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-white border border-emerald-200 hover:border-emerald-300 text-emerald-800 rounded-xl font-medium text-[14px] cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-scan-line text-base" />
                    {t("documentScanner.scannerBtn")}
                  </button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept={DOCUMENT_FILE_ACCEPT}
                  onChange={handleIdentityFile}
                  className="hidden"
                />
              </>
            ) : null}

            <div className="flex flex-col gap-3 mt-8">
              <button
                type="button"
                onClick={() => onDone()}
                className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
              >
                {data.hasPassport === "yes"
                  ? t("docFlow.continue")
                  : hasTrimmedString(value)
                  ? t("docFlow.continue")
                  : t("docFlow.skip")}
              </button>
            </div>
          </>
        )}

        {identityReplaceConfirmOpen ? (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center px-4"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
            onClick={() => setIdentityReplaceConfirmOpen(false)}
            role="presentation"
          >
            <div
              className="w-full max-w-[420px] bg-white rounded-2xl px-5 pt-5 pb-6 shadow-2xl border border-slate-100"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="identity-replace-doc-title"
            >
              <h3 id="identity-replace-doc-title" className="text-[17px] font-semibold text-slate-900 mb-2 text-center">
                {t("docUpload.identityReplaceConfirmTitle")}
              </h3>
              <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6">
                {t("docUpload.identityReplaceConfirmBody")}
              </p>
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => setIdentityReplaceConfirmOpen(false)}
                  className="w-full py-3.5 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[15px] font-semibold cursor-pointer transition-colors touch-manipulation"
                >
                  {t("docUpload.identityReplaceConfirmCancel")}
                </button>
                <button
                  type="button"
                  disabled={isIdentityAiBusy}
                  onClick={() => void confirmReplaceIdentityAndReturnToGate()}
                  className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-45 disabled:pointer-events-none text-white text-[15px] font-semibold cursor-pointer transition-colors touch-manipulation"
                >
                  {t("docUpload.identityReplaceConfirmReplace")}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <MobileDocumentScanner
          open={identityScannerOpen}
          passportExamplesSuite={data.docType === "passport"}
          passportLowPageCountWarn={data.docType === "passport"}
          afterPdfBusyLabelKey="docUpload.identityAiUploadingSecure"
          releaseUiBeforeOnComplete
          onClose={() => setIdentityScannerOpen(false)}
          onComplete={async (pdf) => {
            await ingestIdentityFile(pdf);
          }}
        />

        {identityDocViewerOpen ? (
          <div
            className="fixed inset-0 z-[260] flex flex-col bg-slate-950"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gm-passport-viewer-title"
          >
            <header className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 pt-[max(12px,env(safe-area-inset-top))] bg-slate-900 text-white border-b border-slate-800">
              <h2 id="gm-passport-viewer-title" className="text-[16px] font-semibold truncate flex-1 min-w-0 pr-2">
                {t("docUpload.passportViewerSheetTitle")}
              </h2>
              <button
                type="button"
                onClick={closeIdentityDocumentViewer}
                className="shrink-0 min-h-[44px] px-4 rounded-xl bg-slate-800 text-white text-[15px] font-semibold hover:bg-slate-700 touch-manipulation"
              >
                {t("docUpload.passportViewerClose")}
              </button>
            </header>
            <div className="flex-1 min-h-0 relative bg-slate-900">
              {identityDocViewerLoading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white p-6">
                  <i className="ri-loader-4-line text-4xl animate-spin shrink-0" aria-hidden="true" />
                  <p className="text-[15px] font-medium text-center text-slate-200">
                    {t("docUpload.passportViewOpening")}
                  </p>
                </div>
              ) : identityDocViewerError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white px-6 text-center">
                  <p className="text-[15px] leading-relaxed text-slate-200 max-w-md">{identityDocViewerError}</p>
                  <button
                    type="button"
                    onClick={closeIdentityDocumentViewer}
                    className="min-h-[48px] px-6 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 touch-manipulation"
                  >
                    {t("docUpload.passportViewerClose")}
                  </button>
                </div>
              ) : identityDocViewerUrl && identityDocViewerMime.toLowerCase().includes("pdf") ? (
                <iframe
                  title={String(getIdentityUploadFilename(data.docType) || "PDF")}
                  src={`${identityDocViewerUrl}#zoom=page-width`}
                  className="w-full h-full border-0 bg-slate-100"
                />
              ) : identityDocViewerUrl && identityDocViewerMime.toLowerCase().startsWith("image/") ? (
                <div className="w-full h-full overflow-auto flex items-start justify-center p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
                  <img
                    src={identityDocViewerUrl}
                    alt=""
                    className="w-full max-w-5xl h-auto rounded-lg shadow-xl"
                  />
                </div>
              ) : identityDocViewerUrl ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white px-6 text-center">
                  <p className="text-[15px] leading-relaxed text-slate-200 max-w-md">
                    {t("docUpload.passportViewUnavailable")}
                  </p>
                  <button
                    type="button"
                    onClick={closeIdentityDocumentViewer}
                    className="min-h-[48px] px-6 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 touch-manipulation"
                  >
                    {t("docUpload.passportViewerClose")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {showPhotoGuide && <PassportScanExamplesModal onClose={() => setShowPhotoGuide(false)} />}
      </div>
    );
  }

  // ── Asylum documents — multi-category upload ──
  if (category === "asylum") {
    return (
      <>
        <AsylumUpload
          onBack={onBack}
          onSkipDone={onDone}
          onContinueWithAck={() => openDocumentSectionExitModal("asylum")}
        />
        {sectionExitModalEl}
      </>
    );
  }

  // ── Employment documents — multi-category upload ──
  if (category === "employment") {
    return (
      <>
        <EmploymentUpload
          onBack={onBack}
          onSkipDone={onDone}
          onContinueWithAck={() => openDocumentSectionExitModal("employment")}
        />
        {sectionExitModalEl}
      </>
    );
  }

  // ── Family documents — multi-category upload ──
  if (category === "family") {
    return (
      <>
        <FamilyUpload
          onBack={onBack}
          onSkipDone={onDone}
          onContinueWithAck={() => openDocumentSectionExitModal("family")}
        />
        {sectionExitModalEl}
      </>
    );
  }

  // ── Vulnerability report upload ──
  if (category === "vulnerability") {
    const vulnerabilityReport = data.vulnerabilityReport;
    const vulnerabilityFormUrl =
      "https://storage.readdy-site.link/project_files/08d52403-ed49-4bb0-8496-f80951cc8499/1c497bdf-ea1c-4f0e-8747-b21a279efa45_anexo-ii-placeholder.pdf?v=2c17e23616587822beb04fd58a40ef74";

    const handleVulnerabilityDownload = () => {
      window.open(vulnerabilityFormUrl, "_blank", "noopener,noreferrer");
    };

    const handleVulnerabilityEmail = () => {
      const subject = "Anexo II vulnerability report";
      const body = [
        "Hello,",
        "",
        "Could you please help me complete and sign the Anexo II vulnerability report?",
        "",
        `Form link: ${vulnerabilityFormUrl}`,
      ].join("\n");
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const handleVulnerabilityFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setDocFlowUploadError(null);
      const result = await uploadCaseDocumentFile(ensureCaseSession, "other_supporting", file, {
        supportingDescription: "vulnerability_report",
      });
      if (result.ok === false) {
        setDocFlowUploadError(result.message);
      } else {
        update("vulnerabilityReport", {
          filename: file.name,
          fileSize: formatStayFileSize(file.size),
          documentId: result.documentId ? String(result.documentId).trim() : undefined,
        });
      }
      if (e.target) e.target.value = "";
    };

    const performVulnerabilityDeleteAfterConfirm = async () => {
      setVulnerabilityDeleteBusy(true);
      setDocFlowUploadError(null);
      try {
        const mid = resolveMongoDocumentIdForDelete(vulnerabilityReport);
        const res = await deleteSessionUploadedFileIfApplicable({
          ensureCaseSession,
          caseSessionId,
          mongoDocumentId: mid,
        });
        if (res.ok === false) setDocFlowUploadError(res.message);
        else update("vulnerabilityReport", { filename: "", fileSize: "", documentId: undefined });
      } finally {
        setVulnerabilityDeleteBusy(false);
        setVulnerabilityDeleteOpen(false);
      }
    };

    return (
      <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-6">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
        <button
          type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
        >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            {t("nav.breadcrumbOrganizer")}
        </button>
        </nav>

        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-1">{t("docUpload.vulnHeading")}</h2>
          <p className="text-[13px] text-slate-600 leading-relaxed">{t("docUpload.vulnIntroBody")}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t("docUpload.vulnStep1")}
          </p>
          <p className="text-[14px] text-slate-600 leading-relaxed mb-3">{t("docUpload.vulnStep1Body")}</p>
          <div className="flex gap-2">
              <button
                type="button"
              onClick={handleVulnerabilityDownload}
              className="flex-1 min-h-[44px] px-4 inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[14px] cursor-pointer transition-colors whitespace-nowrap"
              >
              <i className="ri-download-line text-base" />
              {t("docUpload.vulnDownloadBtn")}
              </button>
                <button
                  type="button"
              onClick={handleVulnerabilityEmail}
              className="flex-1 min-h-[44px] px-4 inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[14px] cursor-pointer transition-colors whitespace-nowrap"
                >
              <i className="ri-mail-send-line text-base" />
              {t("docUpload.vulnEmailBtn")}
                </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t("docUpload.vulnStep2")}
          </p>
          <p className="text-[14px] text-slate-600 leading-relaxed mb-3">{t("docUpload.vulnStep2Body")}</p>

          {!vulnerabilityReport.filename ? (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[13px] cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-file-upload-line text-sm" />
                {t("docUpload.chooseFile")}
              </button>
              {/* “Use scanner” duplicate removed */}
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
              <i className="ri-file-check-line text-emerald-600 text-base shrink-0" />
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <p className="text-[12px] font-medium text-emerald-800 truncate">{vulnerabilityReport.filename}</p>
                {vulnerabilityReport.fileSize ? (
                  <span className="text-[11px] text-emerald-700 shrink-0">{vulnerabilityReport.fileSize}</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-[12px] text-emerald-700 underline cursor-pointer shrink-0 whitespace-nowrap"
              >
                {t("docUpload.modalChangeFile")}
              </button>
              <button
                type="button"
                onClick={() => setVulnerabilityDeleteOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0"
                title={t("docUpload.removeDoc")}
              >
                <i className="ri-delete-bin-line text-base" />
              </button>
          </div>
          )}

            <input
              ref={fileRef}
              type="file"
              accept={DOCUMENT_FILE_ACCEPT}
              onChange={handleVulnerabilityFilePick}
              className="hidden"
            />

          {docFlowUploadError ? (
            <p className="mt-3 text-[12px] text-rose-600">{docFlowUploadError}</p>
          ) : null}
        </div>

        <div className="bg-slate-50 rounded-2xl p-4 mb-6">
          <p className="text-[13px] font-semibold text-slate-700 mb-2">{t("docUpload.vulnTipsTitle")}</p>
          <ul className="flex flex-col gap-1.5">
            {(
              [
                "vulnTipSocial",
                "vulnTipNGO",
                "vulnTipForward",
                "vulnTipSigned",
                "vulnTipID",
              ] as const
            ).map((key) => (
              <li key={key} className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-slate-500 text-sm mt-0.5 shrink-0" />
                <p className="text-[12px] text-slate-500 leading-relaxed">{t(`docUpload.${key}`)}</p>
              </li>
            ))}
          </ul>
      </div>

      <div className="mt-4 flex flex-col gap-3">
          <button
            type="button"
            onClick={() =>
              hasTrimmedString(vulnerabilityReport.filename)
                ? openDocumentSectionExitModal("vulnerability")
                : onDone()
            }
            className="w-full min-h-[48px] flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
          >
            {hasTrimmedString(vulnerabilityReport.filename) ? t("docFlow.continue") : t("docFlow.skip")}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full min-h-[48px] flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
          >
            {t("docFlow.backToChecklist")}
          </button>
        </div>
        <DocumentDeleteConfirmModal
          open={vulnerabilityDeleteOpen}
          busy={vulnerabilityDeleteBusy}
          onCancel={() => !vulnerabilityDeleteBusy && setVulnerabilityDeleteOpen(false)}
          onConfirm={() => void performVulnerabilityDeleteAfterConfirm()}
        />
        {sectionExitModalEl}
        </div>
    );
  }

  // ── Other documents — advisor-requested extras ──
  if (category === "other") {
    return (
      <>
        <OtherSupportingDocumentsUpload
          otherDocuments={data.otherDocuments}
          update={update}
          ensureCaseSession={ensureCaseSession}
          caseSessionId={caseSessionId}
          onBack={onBack}
          onSkipDone={onDone}
          onContinueWithAck={() => openDocumentSectionExitModal("other")}
          t={t}
        />
        {sectionExitModalEl}
      </>
    );
  }

  // ── Criminal record — combined briefing + upload ──
  if (category === "criminal") {
    const hasCriminalDocUpload = hasCriminalPageFileUpload(data);
    return (
      <>
        <div className="max-w-[480px] mx-auto px-4 pt-6 pb-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
            <button
              type="button"
              onClick={() => onBack()}
              className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              {t("nav.breadcrumbOrganizer")}
            </button>
          </nav>

          {/* Header */}
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-slate-900 leading-tight mb-0.5">{t("docUpload.criminalTitle")}</h2>
            <p className="text-[13px] text-slate-500 leading-snug mb-2">{t("criminal.uploadPageDesc")}</p>
            <button
              type="button"
              onClick={() => setShowDocGuide(true)}
              className="flex items-center gap-1.5 text-[13px] text-slate-500 font-medium cursor-pointer hover:text-slate-700 transition-colors"
            >
              <i className="ri-image-line text-sm" />
              {t("docPhotoGuide.link")}
            </button>
          </div>

          {docFlowUploadError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">{docFlowUploadError}</p>
          )}

          <CriminalUpload />

          {/* Continue button */}
          <div className="mt-8">
            <button
              type="button"
              onClick={() =>
                hasCriminalDocUpload ? openDocumentSectionExitModal("criminal") : onDone()
              }
              className="w-full min-h-[48px] flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
            >
              {hasCriminalDocUpload ? t("docFlow.continue") : t("docFlow.skip")}
            </button>
          </div>

          {showDocGuide && <DocPhotoGuideModal open={showDocGuide} onClose={() => setShowDocGuide(false)} />}
        </div>
        {sectionExitModalEl}
      </>
    );
  }

  // ── Other single-file upload (denial) ──
  if (SINGLE_FILE_CATEGORIES.includes(category as SingleFileCategory)) {
    const field = FIELD_MAP[category as SingleFileCategory];
    const value = ((data as unknown) as Record<string, unknown>)[field] as string || "";
    const hasDenialUpload = hasTrimmedString(value);
    const singleFileDocumentType = "prior_denial_resolution";

    const performDenialRemoveAfterConfirm = async () => {
      setDenialDeleteBusy(true);
      setDocFlowUploadError(null);
      try {
        const mid = resolveMongoDocumentIdForDelete({
          documentId: data.denialResolutionDocumentId,
          id: data.denialResolutionDocumentId,
        });
        const res = await deleteSessionUploadedFileIfApplicable({
          ensureCaseSession,
          caseSessionId,
          mongoDocumentId: mid,
        });
        if (res.ok === false) setDocFlowUploadError(res.message);
        else {
          update(field as Parameters<typeof update>[0], "");
          update("denialResolutionDocumentId", "");
        }
      } finally {
        setDenialDeleteBusy(false);
        setDenialDeleteOpen(false);
      }
    };

    return (
      <>
        <div className="w-full min-w-0 max-w-[480px] mx-auto px-4 pt-6 pb-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              {t("nav.breadcrumbOrganizer")}
            </button>
          </nav>

          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <i className="ri-upload-cloud-2-line text-emerald-600 text-lg" />
            </div>
            <h2 className="text-[17px] font-semibold text-slate-900 leading-snug">
              {t(`docUpload.${category}Title`)}
            </h2>
          </div>

          {docFlowUploadError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">{docFlowUploadError}</p>
          )}

          <div className="mb-5">
            <UploadOptions
              value={value}
              onChange={async (filename, _d, file) => {
                setDocFlowUploadError(null);
                update(field as Parameters<typeof update>[0], filename);
                if (!String(filename ?? "").trim()) {
                  update("denialResolutionDocumentId", "");
                  return;
                }
                if (!file) return;
                const r = await uploadCaseDocumentFile(ensureCaseSession, singleFileDocumentType, file);
                if (r.ok === false) {
                  setDocFlowUploadError(r.message);
                  update(field as Parameters<typeof update>[0], "");
                  update("denialResolutionDocumentId", "");
                } else if (r.documentId) {
                  update("denialResolutionDocumentId", String(r.documentId).trim());
                }
              }}
              showScanner={false}
              showScannerApps={false}
              t={t}
              onRequestClear={() => setDenialDeleteOpen(true)}
            />
          </div>

          <div className="flex flex-col gap-2 mt-5">
            <button
              type="button"
              onClick={() =>
                hasDenialUpload ? openDocumentSectionExitModal("denial") : onDone()
              }
              className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
            >
              {hasDenialUpload ? t("docFlow.continue") : t("docFlow.skip")}
            </button>
          </div>
        </div>
        <DocumentDeleteConfirmModal
          open={denialDeleteOpen}
          busy={denialDeleteBusy}
          onCancel={() => !denialDeleteBusy && setDenialDeleteOpen(false)}
          onConfirm={() => void performDenialRemoveAfterConfirm()}
        />
        {sectionExitModalEl}
      </>
    );
  }

  // ── Modal handlers ──
  const handleModalFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pendingModalFileRef.current = file;
    setModalFilename(file.name);
  };

  const resetModal = () => {
    setModalDocType("");
    setModalFilename("");
    setExpandedGroup(null);
    setOtherDescription("");
    pendingModalFileRef.current = null;
    if (modalFileRef.current) modalFileRef.current.value = "";
  };

  const handleAddDoc = async () => {
    if (!modalDocType || !modalFilename) return;
    const file = pendingModalFileRef.current;
    if (!file) {
      setDocFlowUploadError("Select a file to upload.");
      return;
    }

    setDocFlowUploadError(null);
    setModalUploading(true);
    try {
      const documentType = mapStayTierKeyToDocumentType(modalDocType);
      const r = await uploadCaseDocumentFile(ensureCaseSession, documentType, file, {
        supportingDescription:
          modalDocType === "Other" ? (otherDescription.trim() || undefined) : undefined,
      });
      if (r.ok === false) {
        setDocFlowUploadError(r.message);
        return;
      }

      const id = genStayDocId();
      const newStay: StayDocument = {
        id,
        filename: file.name,
        fileSize: formatStayFileSize(file.size),
        type: modalDocType,
        description: modalDocType === "Other" ? otherDescription.trim() : "",
        month: "",
        year: "",
        documentId: r.documentId ? String(r.documentId).trim() : undefined,
      };

      update("stayDocuments", [...data.stayDocuments, newStay]);

      const newDoc: UploadedDoc = {
        id,
        filename: file.name,
        docType: modalDocType,
      };

      setUploadedDocs([...uploadedDocs, newDoc]);

      resetModal();
      setShowUploadModal(false);
    } finally {
      setModalUploading(false);
    }
  };

  const requestRemoveResidenceDoc = (index: number) => {
    setResidenceDeleteConfirmIndex(index);
  };

  const performRemoveResidenceDoc = async () => {
    const index = residenceDeleteConfirmIndex;
    if (index == null) return;
    const removed = uploadedDocs[index];
    if (!removed) {
      setResidenceDeleteConfirmIndex(null);
      return;
    }
    const stay = data.stayDocuments?.find((d) => d.id === removed.id);
    const serverId = resolveStayMongoDocumentId(stay);
    setResidenceDeleteBusy(true);
    setDocFlowUploadError(null);
    try {
      const res = await deleteSessionUploadedFileIfApplicable({
        ensureCaseSession,
        caseSessionId,
        mongoDocumentId: serverId,
      });
      if (res.ok === false) {
        setDocFlowUploadError(res.message);
        return;
      }
      const newDocs = uploadedDocs.filter((_, i) => i !== index);
      setUploadedDocs(newDocs);
      if (removed.id) {
        update(
          "stayDocuments",
          data.stayDocuments.filter((d) => d.id !== removed.id)
        );
      }
    } finally {
      setResidenceDeleteBusy(false);
      setResidenceDeleteConfirmIndex(null);
    }
  };

  const ingestResidenceUploadedFile = async (file: File) => {
    if (!modalDocType) return;
    setDocFlowUploadError(null);
    const documentType = mapStayTierKeyToDocumentType(modalDocType);
    const r = await uploadCaseDocumentFile(ensureCaseSession, documentType, file, {
      supportingDescription:
        modalDocType === "Other" ? (otherDescription.trim() || undefined) : undefined,
    });
    if (r.ok === false) {
      setDocFlowUploadError(r.message);
      return;
    }
    const id = genStayDocId();
    const newStay: StayDocument = {
      id,
      filename: file.name,
      fileSize: formatStayFileSize(file.size),
      type: modalDocType,
      description: modalDocType === "Other" ? otherDescription.trim() : "",
      month: "",
      year: "",
      documentId: r.documentId ? String(r.documentId).trim() : undefined,
    };
    update("stayDocuments", [...data.stayDocuments, newStay]);
    const uploadedDoc: UploadedDoc = { id, filename: file.name, docType: modalDocType };
    if (modalDocType === "Other" && otherDescription.trim()) {
      uploadedDoc.description = otherDescription.trim();
    }
    setUploadedDocs([...uploadedDocs, uploadedDoc]);
    setModalDocType("");
    setOtherDescription("");
    setExpandedGroup(null);
    if (residenceFileRef.current) residenceFileRef.current.value = "";
  };

  // ── Residence — final design ──
  return (
    <div className="max-w-[480px] mx-auto px-4 pt-6 pb-6">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[14px] mb-5">
      <button
        type="button"
          onClick={() => onBack()}
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-slate-700 hover:text-slate-900 cursor-pointer transition-colors"
      >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          {t("nav.breadcrumbOrganizer")}
      </button>
      </nav>

      {/* Heading */}
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-slate-900 leading-tight mb-0.5">{t("docUpload.residenceHeading")}</h2>
        <p className="text-[13px] text-slate-500 leading-snug mb-2">{t("docUpload.residenceDesc")}</p>
        <button
          type="button"
          onClick={() => setShowDocGuide(true)}
          className="flex items-center gap-1.5 text-[13px] text-slate-500 font-medium cursor-pointer hover:text-slate-700 transition-colors"
        >
          <i className="ri-image-line text-sm" />
          {t("docPhotoGuide.link")}
        </button>
      </div>

      <div className="h-3" aria-hidden="true" />

      {docFlowUploadError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">{docFlowUploadError}</p>
      )}

      {uploadedDocs.length > 0 && (
        <div className="mb-4">
          {uploadedDocs.map((doc, i) => (
            <div key={i} className="bg-slate-100 rounded-xl border border-slate-300 px-4 py-3 mb-1.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                <i className="ri-file-text-line text-slate-600 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-slate-900 truncate">{doc.filename}</p>
                <p className="text-[12px] text-slate-500">{t(`section7.docType${doc.docType}`)}</p>
              </div>
              <button
                type="button"
                onClick={() => requestRemoveResidenceDoc(i)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0 transition-colors"
                title={t("docUpload.removeDoc")}
                aria-label={t("docUpload.removeDoc")}
              >
                <i className="ri-delete-bin-line text-base" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add a document */}
      <h3 className="text-[15px] font-semibold text-slate-900 mb-1">{t("docUpload.residencePickerTitle")}</h3>
      <p className="text-[12px] text-slate-500 leading-snug mb-3">{t("docUpload.residencePickerSubtitle")}</p>

      {modalDocType ? (
        <div className="mb-3 flex items-center justify-between px-4 py-3 rounded-xl border-2 border-emerald-400 bg-emerald-50">
          <span className="text-[14px] font-semibold text-emerald-800">
            {modalDocType === "Other" ? t("docUpload.residenceGroupOther") : t(`section7.docType${modalDocType}` as Parameters<typeof t>[0])}
          </span>
          <button type="button" onClick={() => { setModalDocType(""); setExpandedGroup(null); setOtherDescription(""); }} className="text-[12px] font-semibold text-emerald-700 underline cursor-pointer hover:text-emerald-900">
            {t("docUpload.residenceChange")}
          </button>
        </div>
      ) : (
      <div className="flex flex-col gap-2 mb-3">
      <button
        type="button"
          onClick={() => { setModalDocType("Padron"); setExpandedGroup(null); }}
          className={`flex flex-col items-start text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${modalDocType === "Padron" ? "bg-emerald-50 border-emerald-400" : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30"}`}
      >
          <span className={`text-[11px] font-semibold uppercase tracking-wide mb-0.5 ${modalDocType === "Padron" ? "text-emerald-700" : "text-emerald-600"}`}>{t("docUpload.residenceFeatured")}</span>
          <span className={`text-[14px] font-semibold ${modalDocType === "Padron" ? "text-emerald-800" : "text-slate-800"}`}>{t("section7.docTypePadron")}</span>
      </button>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <button type="button" onClick={() => setExpandedGroup(expandedGroup === "official" ? null : "official")} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-slate-50">
            <span className="text-[14px] font-semibold text-slate-800">{t("docUpload.residenceGroupOfficial")}</span>
            <i className={`ri-arrow-down-s-line text-slate-500 text-lg transition-transform ${expandedGroup === "official" ? "rotate-180" : ""}`} />
      </button>
          {expandedGroup === "official" && (
            <div className="border-t border-slate-100">
              {[
                { value: "Medical", labelKey: "section7.docTypeMedical" },
                { value: "School", labelKey: "section7.docTypeSchool" },
                { value: "Rental", labelKey: "section7.docTypeRental" },
                { value: "Payslip", labelKey: "section7.docTypePayslip" },
                { value: "Bank", labelKey: "section7.docTypeBank" },
                { value: "Utility", labelKey: "section7.docTypeUtility" },
              ].map((opt) => (
                <button key={opt.value} type="button" onClick={() => { setModalDocType(opt.value); }} className={`w-full text-left px-4 py-2.5 text-[14px] cursor-pointer border-t border-slate-50 first:border-t-0 ${modalDocType === opt.value ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  {t(opt.labelKey as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <button type="button" onClick={() => setExpandedGroup(expandedGroup === "personal" ? null : "personal")} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-slate-50">
            <span className="text-[14px] font-semibold text-slate-800">{t("docUpload.residenceGroupPersonal")}</span>
            <i className={`ri-arrow-down-s-line text-slate-500 text-lg transition-transform ${expandedGroup === "personal" ? "rotate-180" : ""}`} />
          </button>
          {expandedGroup === "personal" && (
            <div className="border-t border-slate-100">
              {[
                { value: "Transfer", labelKey: "section7.docTypeTransfer" },
                { value: "Course", labelKey: "section7.docTypeCourse" },
                { value: "Transport", labelKey: "section7.docTypeTransport" },
                { value: "Police", labelKey: "section7.docTypePolice" },
                { value: "Official", labelKey: "section7.docTypeOfficial" },
              ].map((opt) => (
                <button key={opt.value} type="button" onClick={() => { setModalDocType(opt.value); }} className={`w-full text-left px-4 py-2.5 text-[14px] cursor-pointer border-t border-slate-50 first:border-t-0 ${modalDocType === opt.value ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  {t(opt.labelKey as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" onClick={() => { setModalDocType("Other"); setExpandedGroup(null); }} className={`text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${modalDocType === "Other" ? "bg-emerald-50 border-emerald-400" : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30"}`}>
          <span className={`text-[14px] font-semibold ${modalDocType === "Other" ? "text-emerald-800" : "text-slate-800"}`}>{t("docUpload.residenceGroupOther")}</span>
        </button>
      </div>
      )}

      {modalDocType === "Other" && (
      <div className="mb-3">
          <label className="block text-[13px] font-medium text-slate-500 mb-1">{t("docUpload.residenceOtherLabel")}</label>
          <input
            type="text"
            value={otherDescription}
            onChange={(e) => setOtherDescription(e.target.value)}
            placeholder={t("docUpload.residenceOtherPlaceholder")}
            style={{ fontSize: "16px" }}
            className="w-full text-[14px] text-slate-800 bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-emerald-400 placeholder:text-slate-400"
          />
          <p className="text-[11px] text-slate-500 mt-1">{t("docUpload.residenceOtherHelper")}</p>
      </div>
      )}

      <input
        ref={residenceFileRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file || !modalDocType) return;
          await ingestResidenceUploadedFile(file);
          if (e.target) e.target.value = "";
        }}
      />

      {/* Upload row — disabled until a document type is selected */}
      <div className="flex gap-2 mb-3">
            <button
              type="button"
          onClick={() => residenceFileRef.current?.click()}
          disabled={!modalDocType}
          className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 transition-colors whitespace-nowrap"
            >
          <i className="ri-file-upload-line text-sm" />
          {t("section7.uploadBtn")}
            </button>
              <button
                type="button"
          onClick={() => setResidenceScannerOpen(true)}
          disabled={!modalDocType}
          className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl font-medium text-[13px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 transition-colors whitespace-nowrap"
              >
          <i className="ri-scan-line text-sm" />
          {t("documentScanner.scannerBtn")}
              </button>
      </div>

      {/* Skip advances without modal; Continue opens “mark section complete”. */}
      <div className="mt-8">
        <button
          type="button"
          onClick={() => {
            const hasResidenceDoc = uploadedDocs.length > 0 || (data.stayDocuments?.length ?? 0) > 0;
            if (hasResidenceDoc) openDocumentSectionExitModal("residence");
            else onDone();
          }}
          className="w-full min-h-[48px] flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap"
        >
          {uploadedDocs.length > 0 || (data.stayDocuments?.length ?? 0) > 0
            ? t("docFlow.continue")
            : t("docFlow.skip")}
        </button>
      </div>

      {sectionExitModalEl}

      <DocumentDeleteConfirmModal
        open={residenceDeleteConfirmIndex !== null}
        busy={residenceDeleteBusy}
        onCancel={() => !residenceDeleteBusy && setResidenceDeleteConfirmIndex(null)}
        onConfirm={() => void performRemoveResidenceDoc()}
      />

      {showDocGuide && <DocPhotoGuideModal open={showDocGuide} onClose={() => setShowDocGuide(false)} />}

      <MobileDocumentScanner
        open={residenceScannerOpen}
        onClose={() => setResidenceScannerOpen(false)}
        onComplete={async (pdf) => {
          await ingestResidenceUploadedFile(pdf);
        }}
      />

      {/* Upload modal — bottom sheet */}
      {showUploadModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={() => { setShowUploadModal(false); resetModal(); }}
        >
          <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl px-5 pt-5 pb-8"
            style={{ maxHeight: "85vh", overflowY: "auto", animation: "slideUp 0.28s cubic-bezier(0.32,0.72,0,1)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />

            <h3 className="text-[17px] font-semibold text-slate-900 mb-4">{t("docUpload.modalTitle")}</h3>

            {/* Doc type */}
            <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">{t("docUpload.modalDocType")}</label>
            <div className="mb-4">
              <TieredSelect
              value={modalDocType}
                placeholder={t("docUpload.modalSelectType")}
                onChange={(v) => setModalDocType(v)}
                tiers={[
                  {
                    label: t("section7.acceptedWidget.tier1Label"),
                    options: [{ value: "Padron", label: t("section7.docTypePadron") }],
                  },
                  {
                    label: t("section7.acceptedWidget.tier2Label"),
                    options: [
                      { value: "Rental", label: t("section7.docTypeRental") },
                      { value: "Medical", label: t("section7.docTypeMedical") },
                      { value: "School", label: t("section7.docTypeSchool") },
                      { value: "Bank", label: t("section7.docTypeBank") },
                      { value: "Utility", label: t("section7.docTypeUtility") },
                      { value: "Payslip", label: t("section7.docTypePayslip") },
                    ],
                  },
                  {
                    label: t("section7.acceptedWidget.tier3Label"),
                    options: [
                      { value: "Transfer", label: t("section7.docTypeTransfer") },
                      { value: "Course", label: t("section7.docTypeCourse") },
                      { value: "Transport", label: t("section7.docTypeTransport") },
                      { value: "Police", label: t("section7.docTypePolice") },
                      { value: "Official", label: t("section7.docTypeOfficial") },
                      { value: "Other", label: t("section7.docTypeOther") },
                    ],
                  },
                ]}
              />
            </div>

            {/* File area */}
            <label className="text-[13px] font-medium text-slate-600 mb-1.5 block">{t("docUpload.modalFile")}</label>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 mb-4 text-center">
              <input
                ref={modalFileRef}
                type="file"
                accept="application/pdf,image/*"
                onChange={handleModalFile}
                className="hidden"
              />
              {!modalFilename ? (
                <button
                  type="button"
                  onClick={() => modalFileRef.current?.click()}
                  className="w-full h-10 flex items-center justify-center gap-1.5 bg-white border border-slate-200 rounded-xl text-[13px] font-medium text-slate-600 cursor-pointer hover:border-slate-400 transition-colors"
                >
                  <i className="ri-file-upload-line text-sm" />
                  {t("upload.browseFiles")}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <i className="ri-file-check-line text-slate-500 shrink-0" />
                  <span className="text-[13px] text-slate-700 font-medium truncate flex-1">{modalFilename}</span>
                  <button
                    type="button"
                    onClick={() => { setModalFilename(""); pendingModalFileRef.current = null; if (modalFileRef.current) modalFileRef.current.value = ""; }}
                    className="text-[12px] text-slate-400 hover:text-slate-600 cursor-pointer shrink-0 underline"
                  >
                    {t("docUpload.modalChangeFile")}
                  </button>
                </div>
              )}
            </div>

            {/* Add button */}
            <button
              type="button"
              onClick={() => void handleAddDoc()}
              disabled={!modalDocType || !modalFilename || modalUploading}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl font-semibold text-[15px] cursor-pointer disabled:cursor-not-allowed transition-colors"
            >
              {modalUploading ? "…" : t("docUpload.modalAdd")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
